import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  ShieldAlert,
  X as XIcon,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Minus,
  Activity,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  ChevronDown,
  Watch,
  Users,
  Wrench,
  BookOpen,
} from "lucide-react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import {
  getActivityLog,
  getUnifiedUsers,
  getBookingSlaMetrics,
  getPostHogMetrics,
  getBookingSummary,
  getChatSummary,
  getOnboardingMetrics,
  getGeneralMetrics,
  getBookingDropoff,
  getWorkoutCompletionMetrics,
  getExecutiveSummary,
  getCohorts,
  getRevenueCatOverview,
  type RcOverview,
  type RcPurchaser,
  type Cohort,
  type CohortsResponse,
  type ExecutiveSummary,
  type ExecMetric,
  type BookingDropoff,
  type WorkoutCompletionMetrics,
  type OnboardingMetrics,
  type GeneralMetrics,
  type ActivityLogEntry,
  type UnifiedUser,
  type BookingSlaMetrics,
  type PostHogMetrics,
  type BookingSummary,
  type ChatSummary,
} from "@/lib/api"
import { cn } from "@/lib/utils"
import { ChartCard, InfoHint, SectionHeading } from "@/components/dashboard-cards"
import { PlaceholderCard } from "@/components/PlaceholderCard"
import { NameList } from "@/components/NameList"
import { NamedBar } from "@/components/NamedBar"
import { FeatureEngagement } from "@/components/FeatureEngagement"
import { StaleBanner } from "@/components/StaleBanner"
import { ErrorBoundary } from "@/components/error-boundary"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

type DeepDiveTab = "workouts" | "bookings" | "chat" | "biometrics"

const DAY_MS = 86_400_000

const COLORS = {
  generated: "#10b981",
  extended: "#3b82f6",
  overwritten: "#f59e0b",
  signups: "#3b82f6",
  active: "#10b981",
  trialing: "#3b82f6",
  cancelling: "#f59e0b",
  lapsed: "#f43f5e",
  none: "#cbd5e1",
  violet: "#8b5cf6",
  slate: "#94a3b8",
}

/** Device brands, in the order the backend sorts them (most-worn first). */
const BRAND_COLORS = ["#10b981", "#8b5cf6", "#3b82f6", "#f59e0b", "#94a3b8"]

/**
 * A rule naming a RUN of sections that only mean anything read together, one
 * level above SectionHeading. Quieter than the headings under it on purpose —
 * a group label that shouts competes with the sections it is grouping.
 */
function GroupHeading({ title, description }: { title: string; description: string }) {
  return (
    <div className="border-t pt-6">
      <div className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {title}
      </div>
      <p className="mt-1 max-w-5xl text-lg text-muted-foreground">{description}</p>
    </div>
  )
}

function ScoreCard({
  title,
  value,
  icon: Icon,
  subtitle,
  delta,
  variant = "default",
}: {
  title: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
  subtitle?: string
  /** Week-over-week % change. Positive renders green-up, negative red-down. */
  delta?: number | null
  variant?: "default" | "success" | "warning" | "danger"
}) {
  const variantStyles = {
    default: "bg-card",
    success: "bg-card border-green-200",
    warning: "bg-card border-amber-200",
    danger: "bg-card border-red-200",
  }
  return (
    <div className={cn("rounded-lg border p-4", variantStyles[variant])}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-base text-muted-foreground">{title}</span>
        <Icon className="size-4 text-muted-foreground" />
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {delta != null && Number.isFinite(delta) && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-sm font-medium",
              delta > 0
                ? "text-emerald-600"
                : delta < 0
                  ? "text-red-600"
                  : "text-muted-foreground",
            )}
            title="Week over week"
          >
            {delta > 0 ? (
              <ArrowUp className="size-3" />
            ) : delta < 0 ? (
              <ArrowDown className="size-3" />
            ) : null}
            {Math.abs(Math.round(delta))}%
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-base text-muted-foreground mt-1">{subtitle}</p>
      )}
    </div>
  )
}

type HoverRow = { label: string; value: number | string; color?: string }

/** ScoreCard with a hover breakdown popover; use `side="left"` on right-column tiles. */
function HoverTile({
  title,
  value,
  icon,
  subtitle,
  delta,
  variant,
  onClick,
  hoverTitle,
  rows,
  footer,
  side = "right",
}: {
  title: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
  subtitle?: string
  delta?: number | null
  variant?: "default" | "success" | "warning" | "danger"
  onClick?: () => void
  hoverTitle: string
  rows: HoverRow[]
  footer?: string
  side?: "left" | "right"
}) {
  const numericMax = Math.max(
    1,
    ...rows.filter((r) => typeof r.value === "number").map((r) => r.value as number),
  )
  const card = <ScoreCard title={title} value={value} icon={icon} subtitle={subtitle} delta={delta} variant={variant} />
  const sideCls =
    side === "right"
      ? "left-full ml-2 -translate-x-2 group-hover:translate-x-0"
      : "right-full mr-2 translate-x-2 group-hover:translate-x-0"
  return (
    <div className="relative group">
      {onClick ? (
        <button
          onClick={onClick}
          className="w-full text-left cursor-pointer rounded-lg transition-shadow hover:shadow-md focus:outline-hidden focus:ring-2 focus:ring-primary/40"
          title="Click for detail"
        >
          {card}
        </button>
      ) : (
        card
      )}
      <div
        className={cn(
          "pointer-events-none absolute top-0 z-30 w-56 opacity-0 transition-all duration-150 group-hover:opacity-100",
          sideCls,
        )}
      >
        <div className="rounded-lg border bg-popover shadow-lg p-3">
          <div className="text-sm font-semibold mb-2">{hoverTitle}</div>
          {rows.map((r) => (
            <div key={r.label} className="mb-1.5 last:mb-0">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5">
                  {r.color && <span className="size-2 rounded-sm" style={{ background: r.color }} />}
                  {r.label}
                </span>
                <span className="tabular-nums text-muted-foreground">{r.value}</span>
              </div>
              {r.color && typeof r.value === "number" && (
                <div className="mt-0.5 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(r.value / numericMax) * 100}%`, background: r.color }} />
                </div>
              )}
            </div>
          ))}
          {footer && <div className="mt-2 text-base text-muted-foreground">{footer}</div>}
        </div>
      </div>
    </div>
  )
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: "1px solid hsl(var(--border))",
  background: "hsl(var(--card))",
  color: "hsl(var(--foreground))",
}

function fmtDay(iso: string): string {
  const [, m, d] = iso.split("-")
  return `${Number(m)}/${Number(d)}`
}

/**
 * A timestamp's Pacific calendar day, as `YYYY-MM-DD`.
 *
 * Pacific is this page's day boundary everywhere else — every server-side card
 * cuts days at Pacific midnight (`posthog_day_start`) — so anything bucketed in
 * UTC here filed the last seven hours of every day under tomorrow.
 */
const PACIFIC_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

/** One anchor per Pacific day, at UTC midnight, so day arithmetic can't be
 *  moved by a daylight-saving shift. */
function pacificDayMs(ms: number): number {
  return Date.parse(PACIFIC_DAY.format(new Date(ms)) + "T00:00:00Z")
}

interface Derived {
  aiActivity7d: number
  aiActivityDelta: number | null
  aiTypeBreakdown7d: { generated: number; extended: number; overwritten: number }
  trend: { date: string; generated: number; extended: number; overwritten: number }[]
  /** What one bar covers — a year of daily bars is unreadable, so long windows roll up. */
  trendGrain: "day" | "week"
  trendDays: number
  signups: { week: string; signups: number; total: number }[]
  accessFunnel: { name: string; value: number }[]
  recent: ActivityLogEntry[]
}

function eventCountBetween(
  activity: ActivityLogEntry[],
  fromMs: number,
  toMs: number,
): number {
  return activity.filter((e) => {
    const t = Date.parse(e.created_at)
    return t >= fromMs && t < toMs
  }).length
}

function computeDerived(
  activity: ActivityLogEntry[],
  unified: UnifiedUser[],
  exec: ExecutiveSummary | null,
  trendDays = 30,
): Derived {
  const now = Date.now()
  const today = new Date(now).toISOString().split("T")[0]

  // Past ~10 weeks the bars are thinner than the gaps between them, so roll up
  // to weeks. The window itself still follows the cohort pick either way.
  const trendGrain: "day" | "week" = trendDays > 70 ? "week" : "day"
  const stepDays = trendGrain === "week" ? 7 : 1
  const buckets = new Map<
    string,
    { date: string; generated: number; extended: number; overwritten: number }
  >()
  // Walk backwards in whole steps so the newest bucket always ends today —
  // a partial bucket belongs at the OLD end, where it reads as history.
  for (let i = Math.ceil(trendDays / stepDays) - 1; i >= 0; i--) {
    const startMs = now - ((i + 1) * stepDays - 1) * DAY_MS
    const key = new Date(startMs).toISOString().split("T")[0]
    buckets.set(key, { date: key, generated: 0, extended: 0, overwritten: 0 })
  }
  const keys = Array.from(buckets.keys())
  for (const e of activity) {
    const day = (e.created_at || "").split("T")[0]
    if (!day) continue
    // Newest bucket whose start is on or before this event.
    let idx = -1
    for (let i = keys.length - 1; i >= 0; i--) {
      if (day >= keys[i]) { idx = i; break }
    }
    if (idx < 0 || day > today) continue
    const b = buckets.get(keys[idx])!
    if (e.event_type === "generated") b.generated++
    else if (e.event_type === "extended") b.extended++
    else if (e.event_type === "overwritten") b.overwritten++
  }
  const trend = Array.from(buckets.values())

  const aiActivity7d = eventCountBetween(activity, now - 7 * DAY_MS, now)
  const prev7 = eventCountBetween(activity, now - 14 * DAY_MS, now - 7 * DAY_MS)
  const aiActivityDelta =
    prev7 > 0 ? ((aiActivity7d - prev7) / prev7) * 100 : null

  const aiTypeBreakdown7d = { generated: 0, extended: 0, overwritten: 0 }
  for (const e of activity) {
    const t = Date.parse(e.created_at)
    if (t >= now - 7 * DAY_MS && t < now) {
      if (e.event_type === "generated") aiTypeBreakdown7d.generated++
      else if (e.event_type === "extended") aiTypeBreakdown7d.extended++
      else if (e.event_type === "overwritten") aiTypeBreakdown7d.overwritten++
    }
  }

  // Weeks that END on today's Pacific date, bucketed in day-space. The bars
  // were anchored to the current clock instant, so "Week of Aug 21" actually
  // ran from mid-Aug-20 to mid-Aug-27 in Pacific terms — every label off by the
  // better part of a day, and the newest bar short by however long ago you
  // loaded the page.
  const todayPT = pacificDayMs(now)
  const signupDays: number[] = []
  for (const u of unified) {
    const t = u.created_at ? Date.parse(u.created_at) : NaN
    if (!Number.isNaN(t)) signupDays.push(pacificDayMs(t))
  }
  const WEEK = 7 * DAY_MS
  // All time, not a trailing 8, and cumulative. Plotting each week's own count
  // restarted the line at zero every week, which shows volume but not growth -
  // 10 signups looks identical whether the list is at 100 or 1,000. `total`
  // carries forward so the slope IS the growth rate; `signups` stays on the
  // datum for the tooltip's "+N that week".
  const signups: { week: string; signups: number; total: number }[] = []
  if (signupDays.length > 0) {
    const lastEnd = todayPT + DAY_MS // exclusive end of the current week's bucket
    let earliest = signupDays[0]
    for (const d of signupDays) if (d < earliest) earliest = d
    const weeks = Math.max(1, Math.ceil((lastEnd - earliest) / WEEK))
    const counts = new Array<number>(weeks).fill(0)
    for (const d of signupDays) {
      const i = Math.floor((lastEnd - 1 - d) / WEEK) // 0 = the week ending today
      if (i >= 0 && i < weeks) counts[i]++
    }
    let running = 0
    for (let i = weeks - 1; i >= 0; i--) {
      running += counts[i]
      signups.push({
        week: new Date(lastEnd - (i + 1) * WEEK).toISOString().split("T")[0],
        signups: counts[i],
        total: running,
      })
    }
  }

  // The ratified ladder, in the same words Users & Access uses: Waitlist →
  // Waiting → Approved → Accounts → Users. "Accounts" means an in-app account
  // and nothing else, on this page and everywhere else.
  //
  // The bottom two rungs are read off the executive summary instead of
  // re-derived here: "user" means finished onboarding AND landed on the
  // homepage (shared.is_a_user), and the homepage half lives in PostHog where
  // this page cannot see it — so deriving it here is how "Onboarded 90" ended
  // up beside "52 users". The `onboarded_at` fallback is the same rule, applied
  // server-side in access.list_users, for the seconds before the summary
  // lands. The TOP rung is deliberately NOT read off it: the executive
  // summary's `total_accounts` counts only people who can open the app — an
  // account, or an allowlist row from back when we granted them one at a time
  // — so this bar printed that smaller number under the word "Waitlist".
  const ta = exec?.metrics.total_accounts
  const accessFunnel = [
    { name: "Waitlist", value: unified.length },
    {
      name: "Waiting",
      value: unified.filter((u) => u.in_waitlist && !u.access).length,
    },
    { name: "Approved", value: unified.filter((u) => u.access).length },
    { name: "Accounts", value: ta?.accounts ?? unified.filter((u) => u.is_member).length },
    { name: "Users", value: ta?.users ?? unified.filter((u) => !!u.onboarded_at).length },
  ]

  const recent = [...activity]
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, 12)

  return {
    aiActivity7d,
    aiActivityDelta,
    aiTypeBreakdown7d,
    trend,
    trendGrain,
    trendDays,
    signups,
    accessFunnel,
    recent,
  }
}

/** A named drop-off group. The count is the headline; the names are the point. */
function CallList({
  n,
  title,
  hint,
  note,
  names,
  overflow,
}: {
  n: number
  title: string
  hint: string
  /** A line under the headline for what the count alone gets wrong. */
  note?: React.ReactNode
  names: string[]
  overflow: number
}) {
  const [open, setOpen] = useState(false)
  if (!n) return null
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full text-left flex items-baseline gap-2 cursor-pointer"
      >
        <span className="text-2xl font-semibold tabular-nums text-red-600 shrink-0">
          {n}
        </span>
        <span className="text-base font-medium">{title}</span>
        <span className="text-base text-muted-foreground truncate">— {hint}</span>
        <span className="ml-auto text-base text-muted-foreground shrink-0">
          {open ? "hide names" : "show names"}
        </span>
      </button>
      {note && <p className="text-base text-muted-foreground mt-1.5">{note}</p>}
      {open && (
        <div className="mt-3 pt-3 border-t">
          <NameList title={title} names={names} tone="bad" />
          {overflow > 0 && (
            <p className="text-base text-muted-foreground mt-2">
              + {overflow} more not shown — the full roll is on the Waitlist page.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function AccessCard({ m }: { m: OnboardingMetrics }) {
  const stages = m.access?.stages ?? []
  // Reached the name screen without ever being approved — a hole the waitlist
  // gate has since closed. Shown here because this is the door card and they
  // came in beside it, not through it.
  const unapproved = m.never_approved ?? 0
  if (!stages.length) return null
  // ONE list worth calling, then the member count.
  //
  // "Started an account, never finished" used to sit between them and was cut:
  // the signup card names every one of those people already, on the exact screen
  // they stopped at, which is the part you can act on — checked against live
  // data, all 17 were on that card, 18 of them under "Where you live". Worse, the
  // row was a phone-matched SUBSET (17 of the 20 accounts actually stuck in
  // signup), so the card next to it quoted a bigger, truer number. Two numbers
  // for one group, and the smaller one had less to say. The stage still ships
  // from the backend — `accessChangeRows` reads it as a conversion rate, which is
  // a different question than "who do I call".
  //
  // "Applied for access" is deliberately not shown either: that is the waitlist,
  // it has its own page, and at ~1000 against a member base of ~60 it flattened
  // every bar that mattered into a sliver.
  const approved = stages.find((s) => s.label === "Approved for access")
  const members = stages.find((s) => s.is_final)
  // Subtract, never read the last bar: on a cohort tab the bar is narrowed by
  // APPROVAL date while the member count is narrowed by signup date, so a
  // member approved in an earlier cohort is missing from the bar and the two
  // numbers wouldn't add up.
  const memberCount = m.total_members ?? members?.reached ?? 0

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold text-2xl">Getting in the door</h3>
      <p className="text-base text-muted-foreground mt-0.5 mb-3">
        Invites nobody used, and the user base they turned into.{" "}
        <InfoHint label="How the cohort tabs cut this card">
          On a cohort tab these rows group by approval date — nobody on the list
          has an account yet.
        </InfoHint>
      </p>
      <div className="space-y-2">
        {approved && (
          <CallList
            n={approved.dropped}
            // NOT "never opened the app", which is what this said until it was
            // caught disagreeing with the Landing bar: a profiles row is only
            // written at the NAME screen, so these people may well have opened
            // the app, looked around and quit before ever typing a name.
            title="Approved, never made an account"
            hint="the call list"
            // The "most haven't been told yet, so they aren't drop-off" split
            // was cut on 2026-08-30: it was false. Everyone here was told they
            // were in and took no action, which is the whole point of the list —
            // hedging the recent ones read as "ignore the top of the call list".
            note={
              <>
                Everyone here was told they're in and never made an account. Every
                name carries the day we approved them, oldest first.
              </>
            }
            names={approved.dropped_users}
            overflow={approved.dropped_overflow}
          />
        )}
        {members && (
          <div className="rounded-lg border bg-muted/30 p-3 flex items-baseline gap-2">
            <span className="text-2xl font-semibold tabular-nums text-violet-600 shrink-0">
              {memberCount.toLocaleString()}
            </span>
            <span className="text-base font-medium">users</span>
            {unapproved > 0 && (
              // Not a CallList: these are not people to phone, they're the
              // reconciliation line between this card and the profiles table.
              // Names on hover so the count is checkable without a row for it.
              <span
                className="text-base text-muted-foreground truncate"
                title={(m.never_approved_users ?? []).join("\n")}
              >
                — plus {unapproved} account{unapproved === 1 ? "" : "s"} that
                reached signup without ever being approved
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SignupDropoffCard({ m }: { m: OnboardingMetrics }) {
  const funnel = m.funnel ?? []
  if (!funnel.length) return null

  // Each bar is one STEP, and a step counts whoever got AT LEAST that far. The
  // app walks these steps in one order and lets you skip none, so the backend
  // credits a step to anyone who reached anything after it — which is what makes
  // `completed` on one bar the same number as `reached` on the next. It didn't
  // used to be: three separate tracking holes (the Name screen only instrumented
  // in June, an April→June build that never reported the phone screen, fast
  // double-navigations) made consecutive bars irreconcilable, and the card had to
  // tell the reader not to compare them. Now they compare exactly.
  const rows = funnel.map((f) => ({
    ...f,
    completed: f.is_final ? f.reached : f.reached - f.dropped,
    stopped: f.is_final ? 0 : f.dropped,
    pass: f.reached > 0 ? Math.round(((f.reached - (f.is_final ? 0 : f.dropped)) / f.reached) * 100) : 0,
  }))

  const started = rows[0].reached
  const members = m.total_members ?? 0
  const since = m.funnel_since ?? null
  // Bar widths are a share of everyone who started, so the bars form a staircase
  // down and the step where it falls off a cliff is visible without reading a
  // single number.
  const scale = Math.max(1, started)

  // The weakest step by pass RATE, not by headcount — losing half the people
  // at activities matters more than a large bounce off the landing page every
  // stranger sees. Floor at 10 reached so a 1-of-2 step can't win.
  const weakest = rows
    .filter((r) => !r.is_final && r.reached >= 10 && r.stopped > 0)
    .reduce((a, b) => (b.pass < (a ? a.pass : 101) ? b : a), null as (typeof rows)[number] | null)

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold text-2xl">Onboarding Funnel</h3>
      <p className="text-base text-muted-foreground mt-1 mb-3">
        <strong className="text-foreground">{started} opened signup</strong>,{" "}
        <strong className="text-emerald-600">{members} finished with an account</strong>
        {since && <> — everyone since {since}</>}.{" "}
        <InfoHint label="How to read these bars">
          <span className="text-emerald-600 font-medium">Green got through</span>,{" "}
          <span className="text-red-600 font-medium">red stopped</span>. Click a bar
          for names.
        </InfoHint>
      </p>
      {weakest && (
        <p className="text-base mb-3">
          <span className="font-semibold text-red-600">Fix this step: {weakest.label}</span>
          <span className="text-muted-foreground">
            {" "}— only {weakest.pass}% get through, {weakest.stopped} stopped
            {weakest.dropped_users.length > 0 &&
              `, ${weakest.dropped_users.length} of them named`}
            .
          </span>
        </p>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <NamedBar
            key={r.screen}
            label={r.label}
            value={r.reached}
            rest={r.stopped > 0 ? { value: r.stopped, color: "#ef4444" } : undefined}
            total={scale}
            color={r.is_final ? "#8b5cf6" : "#10b981"}
            caption={
              r.is_final
                ? "have an account"
                : `${r.completed} completed · ${r.stopped} stopped`
            }
            sub={
              r.is_final ? (
                (m.members_untracked ?? 0) > 0 ? (
                  <>
                    Counted from accounts, including {m.members_untracked} from before
                    we tracked screens.
                  </>
                ) : (
                  <>Counted from accounts, not screens.</>
                )
              ) : (
                <>
                  <strong className="text-emerald-600">{r.pass}% got through</strong>
                  {r.anonymous_reached > 0 && (
                    <> · {r.anonymous_reached} here we can't name</>
                  )}
                </>
              )
            }
            hover={
              <>
                <div className="text-base font-semibold mb-2">{r.label}</div>
                <div className="flex gap-4">
                  <NameList
                    title={r.is_final ? "Have an account" : "Completed this step"}
                    names={r.continued_users}
                    tone="good"
                  />
                  {!r.is_final && (
                    <NameList title="Stopped here" names={r.dropped_users} tone="bad" />
                  )}
                </div>
                {r.anonymous_reached > 0 && (
                  <div className="text-sm text-muted-foreground mt-2 pt-2 border-t space-y-1">
                    <p className="font-semibold">
                      + {r.anonymous_reached} here we can't name:
                    </p>
                    {r.unnamed_why ? (
                      <ul className="space-y-1">
                        {r.unnamed_why.never_signed_in > 0 && (
                          <li>
                            <strong>{r.unnamed_why.never_signed_in} never signed in</strong>{" "}
                            — strangers, not people we lost.
                          </li>
                        )}
                        {r.unnamed_why.deleted > 0 && (
                          <li>
                            <strong>{r.unnamed_why.deleted} deleted the account</strong> —
                            mostly our own test runs.
                          </li>
                        )}
                        {r.unnamed_why.no_name_on_file > 0 && (
                          <li>
                            <strong>
                              {r.unnamed_why.no_name_on_file} verified a phone, no name yet
                            </strong>{" "}
                            — reachable, we have the number.
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p>a mix of phones that never signed in and deleted accounts.</p>
                    )}
                  </div>
                )}
              </>
            }
          />
        ))}
      </div>
      {(m.got_in_then_left ?? 0) > 0 && (
        <p className="text-sm text-muted-foreground mt-3 pt-2 border-t">
          <strong>On no bar: {m.got_in_then_left}</strong> finished signup, then deleted
          the account.
          {(m.got_in_then_left_users?.length ?? 0) > 0 && (
            <>
              {" "}
              <InfoHint label="Who they were">
                {m.got_in_then_left_users!.join(", ")}.
              </InfoHint>
            </>
          )}
        </p>
      )}
      {(m.unattributed_sessions ?? 0) > 0 && (
        <p className="text-sm text-muted-foreground mt-2">
          <strong>Also on no bar: {m.unattributed_sessions} sessions</strong>{" "}
          <InfoHint label="What those sessions were">
            Hit a logged-in screen without logging in. App Store review, not people
            we lost.
          </InfoHint>
        </p>
      )}
      {(m.deletions?.members ?? 0) > 0 && (
        <p className="text-sm text-muted-foreground mt-2">
          <strong>
            {m.deletions!.members} user
            {m.deletions!.members === 1 ? "" : "s"} erased their own account
          </strong>
          {" "}
          <InfoHint label="How deletions are counted">
            {m.deletions!.member_dates?.length ? (
              <>Deleted {m.deletions!.member_dates.map(fmtDay).join(", ")}. </>
            ) : null}
            Still counted on the bars above — only our test runs come off.
          </InfoHint>
        </p>
      )}
    </div>
  )
}

// `goals.origin` in plain English. The two that actually happen are the coach
// asking mid-conversation and the member's own Goals screen — both invisible if
// you only count coach cards, which is how a never-shown card came to read as
// "nobody is being asked for this".
const GOAL_ORIGIN_LABELS: Record<string, string> = {
  chat: "in conversation with the coach",
  profile: "on their own Goals screen",
  onboarding: "during signup",
  admin: "entered by an admin",
  backfill: "backfilled by us",
  unknown: "source not recorded",
}

// Members, counted once each at their first goal. Belongs in the tile intro, not
// under a bar: it answers a tile-level question — how do these answers actually
// get here — and its denominator is members, not card views.
function goalOriginLine(filled: Record<string, number>, members: number) {
  const parts = Object.entries(filled)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
  if (!parts.length) return null
  const have = parts.reduce((s, [, n]) => s + n, 0)
  return (
    <p className="text-base text-muted-foreground mb-2">
      Goals on file: {have} of {members} —{" "}
      {parts.map(([k, n]) => `${n} ${GOAL_ORIGIN_LABELS[k] ?? k}`).join(", ")}.
    </p>
  )
}

// One rule for this card: every bar is a count of MEMBERS BLOCKED ON THAT ONE
// THING, the label names that thing, and the names behind the bar are those same
// members. Nothing here is a count of people who DID something — that reading is
// what made the old labels ("Picked their activities") contradict their own
// numbers.
function PlanBlockerCard({ m }: { m: OnboardingMetrics }) {
  const chat = m.chat ?? []
  const total = m.total_members ?? 0
  // EVERY card, in one list, on one denominator (card views). They used to be
  // split: gating cards reported under their blocker's bar, the rest down here.
  // That put two denominators on one row — a bar counts members once at their
  // first hole, a card counts everyone who saw it — and the per-bar line said
  // nothing a reader could act on.
  const sheets = m.sheets ?? []
  if (!chat.length) return null

  const withPlan = chat.find((c) => c.is_success)?.blocked_here ?? 0
  const worst = chat
    .filter((c) => !c.is_success)
    .reduce(
      (a, b) => (b.blocked_here > (a?.blocked_here ?? -1) ? b : a),
      null as (typeof chat)[number] | null,
    )

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold text-2xl">What's blocking a first plan</h3>
      <p className="text-base text-muted-foreground mt-0.5 mb-2">
        <strong>{withPlan} of {total} users have a plan.</strong>{" "}
        <InfoHint label="How to read these bars">
          Each bar is the first answer the other {total - withPlan} are missing —
          one user, one bar. Click a bar for names.
          {chat.some((c) => c.key === "location") && (
            <>
              {" "}
              A bar for <strong>where they live</strong> means signup let someone
              through without one. That's a signup bug, not the coach.
            </>
          )}
        </InfoHint>
      </p>
      {goalOriginLine(chat.find((c) => c.key === "goal")?.filled_by ?? {}, total)}
      {(m.stalled_in_signup ?? 0) > 0 && (
        <p className="text-base text-muted-foreground mb-2">
          {m.stalled_in_signup} more never finished signup{" "}
          <InfoHint label="Where they are instead">
            They're on the funnel above, not here.
            {(m.wiped_onboarding ?? 0) > 0 && (
              <>
                {" "}
                {m.wiped_onboarding} are founding members the July 10 wipe emptied;
                none has reopened the app since.
              </>
            )}
          </InfoHint>
        </p>
      )}
      {worst && worst.blocked_here > 0 && (
        <p className="text-sm mb-3">
          <span className="font-semibold text-amber-600">
            Biggest blocker: {worst.label.replace(/^Missing /, "missing ")} —{" "}
            {worst.blocked_here} users
          </span>
          <span className="text-muted-foreground"> — fix this one first.</span>
        </p>
      )}

      <div className="space-y-3">
        {chat.map((c) => (
          <NamedBar
            key={c.key}
            label={c.label}
            value={c.blocked_here}
            total={Math.max(1, total)}
            color={c.is_success ? "#10b981" : c.blocked_here > 0 ? "#f59e0b" : "#cbd5e1"}
            caption={
              c.is_success
                ? "users have a plan"
                : c.blocked_here === 0
                  ? "nobody is blocked on this"
                  : "users blocked on this"
            }
            // The only per-bar note left is the one that changes what you'd do
            // next. Every bar's label already says what's missing and its count
            // already says how many — a line restating that, or reporting a card
            // on a different denominator, was noise under every row.
            sub={
              c.key === "no_plan_yet" ? (
                <>
                  Every answer is on file — this is a generation failure, not a
                  missing answer.
                </>
              ) : undefined
            }
            hover={
              <>
                <div className="text-sm font-semibold mb-2">
                  {c.is_success ? "Has a plan" : `Blocked on: ${c.label}`}
                </div>
                <NameList
                  title={c.is_success ? "Users" : "Blocked here"}
                  names={c.users}
                  tone={c.is_success ? "good" : "bad"}
                />
                {!c.users.length && (
                  <p className="text-base text-muted-foreground">
                    No user is blocked on this.
                  </p>
                )}
              </>
            }
          />
        ))}
      </div>
      {/* Every card at zero (true of cohort 2) draws seven identical grey bars
          that say nothing. One sentence says the same thing. */}
      {sheets.length > 0 && !sheets.some((s) => s.saw > 0) && (
        <p className="mt-5 pt-4 border-t text-base text-muted-foreground">
          None of the coach's {sheets.length} question cards has been shown to this
          group — the answers on file arrived in conversation instead.
        </p>
      )}
      {sheets.some((s) => s.saw > 0) && (
        <div className="mt-5 pt-4 border-t">
          <h4 className="font-semibold text-base">The cards the coach asks</h4>
          <p className="text-base text-muted-foreground mt-0.5 mb-3">
            Card views, not users.{" "}
            <InfoHint label="How to read these cards">
              Green finished the card, red saw it and didn't. <em>Fills a blocker</em>{" "}
              means the card collects an answer a plan needs. Click for names.
            </InfoHint>
          </p>
          <div className="space-y-3">
            {sheets.map((s) => {
              const dropped = s.dropped_users.length
              return (
                <NamedBar
                  key={s.key}
                  label={s.gates_plan ? `${s.label} — fills a blocker` : s.label}
                  value={s.saw}
                  rest={dropped > 0 ? { value: dropped, color: "#ef4444" } : undefined}
                  total={Math.max(1, s.saw)}
                  color={s.saw === 0 ? "#cbd5e1" : "#10b981"}
                  caption={
                    s.saw === 0
                      ? "not shown yet"
                      : `${s.completed} completed · ${dropped} dropped`
                  }
                  hover={
                    <>
                      <div className="text-sm font-semibold mb-2">{s.label}</div>
                      <div className="flex gap-4">
                        <NameList title="Saw this card" names={s.saw_users} tone="good" />
                        <NameList
                          title="Completed"
                          names={s.completed_users}
                          tone="good"
                        />
                        <NameList
                          title="Saw it, did not finish"
                          names={s.dropped_users}
                          tone="bad"
                        />
                      </div>
                    </>
                  }
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function MemberRosterCard({ m, rc }: { m: OnboardingMetrics; rc: RcOverview | null }) {
  const [q, setQ] = useState("")
  const [sort, setSort] = useState<"signup" | "active" | "name">("signup")
  const [only, setOnly] = useState<"all" | "members" | "stalled" | "active" | "no_plan">(
    "all",
  )
  // Subscription lives in its own filter, opened from the column header, because
  // that's the column it filters. The general dropdown next to the search box
  // stays for everything that isn't a subscription.
  const [subFilter, setSubFilter] = useState<AccountTypeKey | "all">("all")
  const [subMenu, setSubMenu] = useState(false)

  // Keyed on app_user_id, which IS the profile id for every identified customer.
  const rcById = new Map<string, RcPurchaser>(
    (rc?.real_purchasers ?? []).map((p) => [p.id, p]),
  )

  const all = (m.members ?? []).map((x) => ({
    ...x,
    rc: rcById.get(x.id),
    type: accountType(x, rcById.get(x.id)),
  }))

  // Counts for the header menu and the legend. Only types someone actually is
  // get explained — a glossary of states nobody is in is just noise.
  const typeCounts = new Map<AccountTypeKey, number>()
  for (const x of all) typeCounts.set(x.type, (typeCounts.get(x.type) ?? 0) + 1)
  const presentTypes = ACCOUNT_TYPES.filter((t) => (typeCounts.get(t.key) ?? 0) > 0)
  const staleCopies = all.filter(
    (x) =>
      x.rc && x.type !== "founding" && OUR_STATUS_TYPE[x.status ?? "free"] !== x.type,
  ).length
  const brands = m.wearable_brands ?? []
  // Two tallies that disagree on purpose: 22 members WEAR a device while the
  // brand rows add up to 24 DEVICES, because two members wear two. Both are
  // stated so neither reads as the other's arithmetic being wrong.
  //
  // The denominator is MEMBERS, not accounts. An account still stuck at the
  // paywall never reached the screen that connects a device, so "22 of 67"
  // was measuring the connect rate against a population that couldn't connect.
  const memberRows = all.filter((x) => x.got_in)
  const memberTotal = m.total_members ?? memberRows.length
  const connected = memberRows.filter((x) => x.wearables.length).length
  const devices = all.reduce((n, x) => n + x.wearables.length, 0)
  const twoDevices = all.filter((x) => x.wearables.length > 1).length
  // Anyone wearing a device who never cleared the paywall sits outside that
  // denominator — say so rather than quietly dropping them.
  const connectedOutside = all.filter((x) => !x.got_in && x.wearables.length).length

  const rows = all
    .filter((x) => {
      if (only === "members" && !x.got_in) return false
      if (only === "stalled" && x.got_in) return false
      if (only === "active" && !x.active_today) return false
      if (only === "no_plan" && x.has_season) return false
      if (subFilter !== "all" && x.type !== subFilter) return false
      const needle = q.trim().toLowerCase()
      if (!needle) return true
      return (
        x.name.toLowerCase().includes(needle) ||
        (x.plan ?? "").toLowerCase().includes(needle) ||
        x.wearables.join(" ").toLowerCase().includes(needle)
      )
    })
    .sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name)
      if (sort === "active") return (b.last_active ?? "").localeCompare(a.last_active ?? "")
      return (b.signup ?? "").localeCompare(a.signup ?? "")
    })

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          {/* The counts this card used to open with now sit in the section
              heading above it — same sentence, moved up when Accounts became a
              section of its own. What's left here is only what the heading
              doesn't say. */}
          <h3 className="font-semibold text-2xl">Everyone, by name</h3>
          <p className="text-base text-muted-foreground mt-0.5">
            The {m.total_members ?? 0} who finished onboarding and the{" "}
            {m.stalled_in_signup ?? 0} marked <em>in signup</em> who didn't, in one
            list.{" "}
            <InfoHint label="What “active today” means">
              <strong>Active today</strong> = did anything in the app today — the
              “Doing” column says what.
            </InfoHint>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:min-w-md">
          <ScoreCard
            title="Active today"
            value={m.active_today ?? 0}
            icon={Activity}
            subtitle={`of ${memberTotal} users`}
          />
          <HoverTile
            title="Wearable connected"
            value={`${connected} of ${memberTotal}`}
            icon={Watch}
            subtitle={`${devices} device${devices === 1 ? "" : "s"} linked${
              twoDevices ? ` · ${twoDevices} wear two` : ""
            }`}
            hoverTitle="Devices by brand"
            side="left"
            rows={brands.map((b, i) => ({
              label: prettyBrand(b.provider),
              value: b.people,
              color: BRAND_COLORS[i % BRAND_COLORS.length],
            }))}
            footer={
              `${devices} devices across ${connected} users.` +
              (connectedOutside
                ? ` ${connectedOutside} more never finished onboarding, so they're outside this count.`
                : "")
            }
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 my-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, plan or device…"
          className="flex-1 min-w-48 h-8 rounded-md border bg-background px-2 text-sm"
        />
        <select
          value={only}
          onChange={(e) => setOnly(e.target.value as typeof only)}
          className="h-8 rounded-md border bg-background px-2 text-sm"
        >
          <option value="all">Everyone</option>
          <option value="members">Got into the app</option>
          <option value="stalled">Still stuck in signup</option>
          <option value="active">Active today</option>
          <option value="no_plan">No workout plan</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          className="h-8 rounded-md border bg-background px-2 text-sm"
        >
          <option value="signup">Newest signup</option>
          <option value="active">Last active</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      {/* What each account type in this table actually means, in money terms.
          Above the table, not below it — the tags are unreadable without it. */}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 rounded border bg-muted/30 p-3 text-sm sm:grid-cols-2">
        {presentTypes.map((t) => (
          <div key={t.key} className="flex gap-2">
            {/* self-start: without it the flex row stretches the coloured tag
                down the full height of its wrapped description. */}
            <dt className={cn("shrink-0 self-start rounded px-1.5 font-medium", t.cls)}>
              {t.label}
            </dt>
            <dd className="text-muted-foreground">{t.what}</dd>
          </div>
        ))}
      </dl>
      {staleCopies > 0 && (
        <p className="text-sm text-muted-foreground">
          {staleCopies} row{staleCopies === 1 ? "" : "s"} marked{" "}
          <span className="font-medium">*</span>{" "}
          <InfoHint label="What the asterisk means">
            Our copy disagrees with RevenueCat, so RevenueCat is shown. A missed
            webhook is the usual cause.
          </InfoHint>
        </p>
      )}

      {/* The filter menu lives inside this scroll box, so a short list (one row
          after filtering) clipped it. Floor the box to the menu's own height
          while it is open; the box stays dynamic the rest of the time. */}
      <div
        className="max-h-128 overflow-auto rounded border"
        style={
          subMenu
            ? {
                minHeight: `${
                  (presentTypes.length + 1) * MENU_ROW_REM + MENU_CHROME_REM
                }rem`,
              }
            : undefined
        }
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
            <tr className="text-left">
              <th className="p-2 font-medium">Name</th>
              <th className="p-2 font-medium relative">
                <button
                  onClick={() => setSubMenu((v) => !v)}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground/70"
                  title="Filter by account type"
                >
                  Subscription
                  {subFilter !== "all" && (
                    <span className="rounded bg-foreground/10 px-1 text-sm font-normal">
                      {ACCOUNT_TYPE_BY_KEY[subFilter].label}
                    </span>
                  )}
                  <ChevronDown className={cn("size-3.5", subMenu && "rotate-180")} />
                </button>
                {subMenu && (
                  <>
                    {/* Closes on any outside click without an event listener. */}
                    <button
                      className="fixed inset-0 z-10 cursor-default"
                      aria-label="Close filter"
                      onClick={() => setSubMenu(false)}
                    />
                    <div className="absolute left-1 top-full z-20 mt-1 w-56 rounded-md border bg-card py-1 shadow-lg">
                      {[
                        { key: "all" as const, label: "All account types", n: all.length },
                        ...presentTypes.map((t) => ({
                          key: t.key,
                          label: t.label,
                          n: typeCounts.get(t.key) ?? 0,
                        })),
                      ].map((o) => (
                        <button
                          key={o.key}
                          onClick={() => {
                            setSubFilter(o.key)
                            setSubMenu(false)
                          }}
                          className={cn(
                            "flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted",
                            subFilter === o.key && "font-semibold",
                          )}
                        >
                          <span>{o.label}</span>
                          <span className="ml-auto tabular-nums text-muted-foreground">
                            {o.n}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </th>
              <th className="p-2 font-medium">Last seen</th>
              <th className="p-2 font-medium">Signed up</th>
              <th className="p-2 font-medium">Workout plan</th>
              <th className="p-2 font-medium">Doing</th>
              <th className="p-2 font-medium">Device</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.id} className="border-t hover:bg-muted/30">
                <td className="p-2">
                  <div className="flex items-center gap-1.5">
                    {x.active_today && (
                      <span
                        className="size-1.5 rounded-full bg-emerald-500 shrink-0"
                        title="Active today"
                      />
                    )}
                    <span className="font-medium truncate">{x.name}</span>
                    {!x.got_in && (
                      <span
                        className="text-sm rounded bg-muted px-1 py-px text-muted-foreground shrink-0"
                        title="Started an account but never cleared the paywall — not counted as a user"
                      >
                        in signup
                      </span>
                    )}
                  </div>
                  <div className="text-base text-muted-foreground">
                    {x.messages > 0 ? `${x.messages} chat messages` : "never messaged"}
                  </div>
                </td>
                <td className="p-2">
                  <AccountChip plan={x.plan} status={x.status} rc={x.rc} />
                </td>
                <td className="p-2 tabular-nums text-muted-foreground whitespace-nowrap">
                  {x.last_active ? fmtWhen(x.last_active) : "—"}
                </td>
                <td className="p-2 tabular-nums text-muted-foreground whitespace-nowrap">
                  {x.signup ? fmtWhen(x.signup) : "—"}
                </td>
                <td className="p-2">
                  {x.has_season ? (
                    <span className="text-emerald-600">Yes</span>
                  ) : (
                    <span className="text-muted-foreground">No</span>
                  )}
                </td>
                <td className="p-2 text-muted-foreground">
                  {x.doing.length ? x.doing.slice(0, 3).join(", ") : "—"}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {x.wearables.length ? x.wearables.map(prettyBrand).join(", ") : "—"}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-muted-foreground">
                  No users match that filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  )
}

/** Humanize Terra provider slugs for table display. */
function prettyBrand(p: string) {
  return p
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ")
}

function fmtWhen(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })
}

/** The only plan names the webhook ever writes (services/revenuecat_service.py). */
const PLAN_NAMES: Record<string, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  annual: "Annual",
  founding_member: "Founding",
}

/**
 * The account types this dashboard recognises, in the order they're offered as
 * a filter, each with the plain-English answer to "what does this mean?" that
 * feeds the legend under the table.
 *
 * These are DELIBERATELY not the raw `subscription_status` values. Two of those
 * read wrong to anyone who isn't holding the webhook code:
 *
 *  - `expired` didn't fail or break. Every expired row in production had
 *    auto-renew switched OFF at RevenueCat before it ran out, which is a
 *    CANCELLATION that has since run its course. So it's labelled Cancelled.
 *  - `active` is written for trials AND for real payers, because the webhook's
 *    renewal handler flattens both to `active`. A trial is free and rolls over
 *    into paying on its own, so calling it Paying overstates revenue. RevenueCat
 *    can tell them apart; `accountType` below prefers it when it's loaded.
 */
/* Height of the account-type filter menu, in rem, matching the classes on the
   menu itself: one option is py-1.5 (0.375 × 2) around a 1.25rem line, and the
   menu adds py-1 + mt-1 below a ~2.25rem sticky header row. Used to floor the
   scroll box so the open menu is never clipped by it. */
const MENU_ROW_REM = 2
const MENU_CHROME_REM = 4.5

const ACCOUNT_TYPES = [
  {
    key: "founding",
    bar: "#8b5cf6",
    label: "Founding",
    cls: "bg-violet-100 text-violet-700 border-violet-200",
    what: "Comped for life. Never billed, so never appears in RevenueCat.",
  },
  {
    key: "paying",
    bar: "#10b981",
    label: "Paying",
    cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
    what: "Real money, billing now. This is the subscriber count.",
  },
  {
    key: "trial",
    bar: "#3b82f6",
    label: "Free trial",
    cls: "bg-blue-100 text-blue-700 border-blue-200",
    what: "Free right now, worth $0. Rolls over into Paying unless they cancel first.",
  },
  {
    key: "cancelling",
    bar: "#f59e0b",
    label: "Cancelling",
    cls: "bg-amber-100 text-amber-700 border-amber-200",
    what: "Turned auto-renew off but still has access until the term ends.",
  },
  {
    key: "payment_failed",
    bar: "#fb923c",
    label: "Payment failed",
    cls: "bg-orange-100 text-orange-700 border-orange-200",
    what: "Card was declined. Still let in while the store retries.",
  },
  {
    key: "cancelled",
    bar: "#f43f5e",
    label: "Cancelled",
    cls: "bg-rose-100 text-rose-700 border-rose-200",
    what: "Paid before, access has run out. Locked out — this is churn.",
  },
  {
    key: "trial_ended",
    bar: "#94a3b8",
    label: "Trial ended",
    cls: "bg-slate-200 text-slate-700 border-slate-300",
    what: "Trial ran out without converting. Never paid us, so not churn.",
  },
  {
    key: "none",
    bar: "#cbd5e1",
    label: "Never subscribed",
    cls: "bg-muted text-muted-foreground border-transparent",
    what: "No subscription record at all — usually still sitting at the paywall.",
  },
] as const

type AccountTypeKey = (typeof ACCOUNT_TYPES)[number]["key"]

const ACCOUNT_TYPE_BY_KEY = Object.fromEntries(
  ACCOUNT_TYPES.map((t) => [t.key, t]),
) as Record<AccountTypeKey, (typeof ACCOUNT_TYPES)[number]>

/** What our own `profiles.subscription_status` mirror maps to. */
const OUR_STATUS_TYPE: Record<string, AccountTypeKey> = {
  active: "paying",
  trialing: "trial",
  cancelling: "cancelling",
  past_due: "payment_failed",
  expired: "cancelled",
  free: "none",
}

/** What RevenueCat's own lifecycle state maps to. Server-derived — see RcPurchaser. */
const RC_STATE_TYPE: Record<RcPurchaser["state"], AccountTypeKey> = {
  active: "paying",
  cancelling: "cancelling",
  billing_issue: "payment_failed",
  trialing: "trial",
  churned: "cancelled",
  trial_expired: "trial_ended",
}

/**
 * One account type per member, preferring RevenueCat over our own mirror.
 *
 * `profiles.subscription_status` is a webhook-fed COPY of RevenueCat, and a copy
 * can be wrong in two ways we've both seen in production: a status that never
 * got its expiry event and still reads `active` months after RevenueCat expired
 * it, and a trial flattened to `active` by the renewal handler. So when the
 * RevenueCat sweep has loaded, its state wins. Founding members are checked
 * first — they never buy anything, so RevenueCat has no record of them at all.
 */
function accountType(
  row: { plan: string | null; status: string | null },
  rc?: RcPurchaser,
): AccountTypeKey {
  if (row.plan === "founding_member" && row.status !== "expired") return "founding"
  if (rc) return RC_STATE_TYPE[rc.state]
  return OUR_STATUS_TYPE[row.status ?? "free"] ?? "none"
}

/**
 * Account type in one chip, with the plan they bought when we know it. Hover for
 * the raw values on both sides — that tooltip is how a disagreement between our
 * database and RevenueCat gets diagnosed instead of argued about.
 */
function AccountChip({
  plan,
  status,
  rc,
}: {
  plan: string | null
  status: string | null
  rc?: RcPurchaser
}) {
  const key = accountType({ plan, status }, rc)
  const type = ACCOUNT_TYPE_BY_KEY[key]
  const name = PLAN_NAMES[plan ?? ""]
  // The plan name adds nothing to these two — it's the same word, or gone.
  const showName = name && key !== "founding" && key !== "none"
  // Star = RevenueCat overruled our own copy. Only when RevenueCat is what
  // decided this chip: a founding member is comped in our database and unknown
  // to RevenueCat, so a disagreement there isn't RevenueCat winning.
  const stale = rc && key !== "founding" && OUR_STATUS_TYPE[status ?? "free"] !== key
  return (
    <span
      title={
        `${type.what}\n\nour database — plan: ${plan ?? "none"} · status: ${status ?? "none"}` +
        (rc ? `\nrevenuecat — ${rc.state}` : "\nrevenuecat — not loaded") +
        (stale ? "\n\nOur copy disagrees; RevenueCat is shown." : "")
      }
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-sm font-medium whitespace-nowrap",
        type.cls,
      )}
    >
      {showName ? `${type.label} · ${name}` : type.label}
      {stale && <span className="opacity-60">*</span>}
    </span>
  )
}

function OnboardingMetricsSection({ m }: { m: OnboardingMetrics | null }) {
  if (!m || !m.configured || m.error) {
    return (
      <PlaceholderCard
        title="Onboarding Metrics"
        need={
          m && !m.configured
            ? "Ready — set POSTHOG_PERSONAL_API_KEY + POSTHOG_PROJECT_ID on the backend."
            : "Couldn't load onboarding metrics this load."
        }
      />
    )
  }
  const ie = m.internal_excluded
  return (
    <div className="space-y-4">
      {/*
        Above the exclusion note on purpose: if these numbers are old, that is
        the first thing to know about them. Only the PostHog half (the signup
        funnel) can be stale — the access stages and the named roster come from
        our own database and are always current.
      */}
      <StaleBanner asOf={m.stale_as_of}>
        The access stages and the named roster come from our own database and are
        current.
      </StaleBanner>
      {ie && (ie.sessions > 0 || ie.accounts > 0) && (
        <p className="text-base text-muted-foreground">
          Out of every number below: <strong>{ie.sessions} test sessions</strong>
          {ie.accounts > 0 && <> and <strong>{ie.accounts} test accounts</strong></>}{" "}
          <InfoHint label="What gets excluded">
            Our named dry runs and the perf-test bot. Teammates who onboarded stay
            in.
          </InfoHint>
        </p>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <AccessCard m={m} />
        <SignupDropoffCard m={m} />
      </div>
    </div>
  )
}

/**
 * Who is paying us, who is on a free trial, and who left — over PEOPLE, one
 * bar each, every bar clickable for the names.
 *
 * The pie this replaced was read straight off `profiles.subscription_status`,
 * which stores a payer and a free trial as the same word ("active") and cannot
 * tell a declined card from a finished cancellation. So it drew "Paying" over a
 * pile that was part trials, and lumped churn in with billing failures — the
 * two states with completely different things to do about them. This card runs
 * the same `accountType` the roster's chips do, so RevenueCat overrules our
 * mirror where they disagree and the bars here always match the chips below.
 */
function SubscriptionMixCard({ m, rc }: { m: OnboardingMetrics; rc: RcOverview | null }) {
  const rcById = new Map<string, RcPurchaser>(
    (rc?.real_purchasers ?? []).map((p) => [p.id, p]),
  )
  const rows = m.members ?? []
  // Which slice's roster is open. A slice is thin and a hover card over it is
  // fiddly, so the names land under the chart on a click instead.
  const [picked, setPicked] = useState<AccountTypeKey | null>(null)
  if (!rows.length) return null

  const byType = new Map<AccountTypeKey, string[]>()
  for (const x of rows) {
    const k = accountType(x, rcById.get(x.id))
    byType.set(k, [...(byType.get(k) ?? []), x.name])
  }
  // Only states someone is actually in. A zero slice is a glossary entry, not a
  // measurement, and this card is already carrying eight possible ones.
  const present = ACCOUNT_TYPES.filter((t) => (byType.get(t.key)?.length ?? 0) > 0)
  const total = rows.length
  // Access right now, whatever the billing story: the four states that still
  // let someone into the app. It's the number that answers "how many people can
  // use this today", which no single slice does.
  const withAccess = (["founding", "paying", "trial", "cancelling"] as const).reduce(
    (n, k) => n + (byType.get(k)?.length ?? 0),
    0,
  )
  const paying = byType.get("paying")?.length ?? 0
  const slices = present.map((t) => ({
    key: t.key,
    name: t.label,
    value: byType.get(t.key)?.length ?? 0,
    color: t.bar,
  }))
  const pickedType = picked ? present.find((t) => t.key === picked) ?? null : null
  const pickedNames = picked ? byType.get(picked) ?? [] : []

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold text-2xl">Subscription mix</h3>
      <p className="text-lg text-muted-foreground mt-1">
        {paying} paying of {total} accounts · {withAccess} can open the app today.
        Click a slice for names.{" "}
        <InfoHint label="What each state means">
          <ul className="space-y-1 list-disc pl-4">
            {present.map((t) => (
              <li key={t.key}>
                <strong>{t.label}</strong> — {t.what}
              </li>
            ))}
            <li>
              RevenueCat wins over our own copy where the two disagree, so these
              slices match the chips in the list below exactly.
            </li>
          </ul>
        </InfoHint>
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="h-[200px] w-[200px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={80}
                paddingAngle={2}
                // By index, not by the datum: recharts types a slice's `key` as
                // React's own Key, which is not our union.
                onClick={(_d, i) => {
                  const k = slices[i]?.key
                  setPicked((cur) => (k && k !== cur ? k : null))
                }}
                style={{ cursor: "pointer" }}
              >
                {slices.map((sl) => (
                  <Cell
                    key={sl.key}
                    fill={sl.color}
                    // The picked slice keeps its colour; the rest step back, so
                    // the chart says which roster is open without a caption.
                    opacity={!picked || picked === sl.key ? 1 : 0.3}
                    stroke={picked === sl.key ? "hsl(var(--foreground))" : undefined}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v, n) => [`${v} of ${total}`, String(n)]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="min-w-[180px] flex-1 space-y-1">
          {slices.map((sl) => (
            <button
              key={sl.key}
              type="button"
              onClick={() => setPicked((cur) => (cur === sl.key ? null : sl.key))}
              className={cn(
                "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-base hover:bg-muted",
                picked === sl.key && "bg-muted",
              )}
            >
              <span className="size-2.5 shrink-0 rounded-sm" style={{ background: sl.color }} />
              <span className="truncate">{sl.name}</span>
              <span className="ml-auto shrink-0 tabular-nums">
                {sl.value}{" "}
                <span className="text-muted-foreground">
                  ({Math.round((sl.value / total) * 100)}%)
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
      {pickedType && (
        <div className="mt-4 border-t pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="text-sm text-muted-foreground">{pickedType.what}</div>
            <button
              type="button"
              onClick={() => setPicked(null)}
              className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
          <div className="mt-3">
            <NameList
              title={pickedType.label}
              names={[...pickedNames].sort((a, b) => a.localeCompare(b))}
              tone={
                picked === "paying" || picked === "founding" || picked === "trial"
                  ? "good"
                  : "bad"
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The blocker card, promoted out of Onboarding Metrics into a section of its
 * own. Clearing signup and having a plan are two different finish lines, and
 * under a shared heading the second read as a footnote to the first — which is
 * backwards, since a member with no plan has nothing to open the app for.
 *
 * Same data, same guard, same props — only its position on the page changed.
 */
function PlanBlockerSection({ m }: { m: OnboardingMetrics | null }) {
  if (!m || !m.configured || m.error) return null
  return <PlanBlockerCard m={m} />
}

/**
 * The roster, promoted out of Onboarding Metrics into a section of its own so
 * the page reads flow → who → what they do: how people get in, then WHO got in
 * by name, then what those people do once inside. It was the last card of the
 * onboarding section, which buried the one table anyone actually opens.
 *
 * Same data, same guard, same props — only its position on the page changed.
 */
function AccountsSection({
  m,
  rc,
}: {
  m: OnboardingMetrics | null
  rc: RcOverview | null
}) {
  if (!m || !m.configured || m.error) return null
  return <MemberRosterCard m={m} rc={rc} />
}

// Same rule as the signup drop-off card above: one subject, counted ONCE, on the
// deepest stage it reached. The subject here is a booking ATTEMPT, not a person,
// so the bars add up to the number of attempts and are never subtracted from
// each other.
function BookingDropoffCard({ b }: { b: BookingDropoff }) {
  const funnel = b.funnel ?? []
  if (!funnel.length) return null

  const total = b.total_attempts ?? 0
  const booked = b.booked ?? 0
  // Everything this window found that no bar shows. The card claimed the bars
  // added up to everything while printing only two of these four, so on a cohort
  // tab a hundred attempts went missing between the prose and the footnote.
  const outside =
    (b.other_cohort_attempts ?? 0) +
    (b.unattributed_attempts ?? 0) +
    (b.unstaged_attempts ?? 0) +
    (b.test_included ? 0 : (b.test_attempts ?? 0))
  // Ranked by raw count, not rate: unlike signup there's no honest "reached"
  // number per stage (cancelled and failed are outcomes, not depths), so a pass
  // rate here would be invented. The biggest pile is the honest answer.
  const worst = funnel
    .filter((f) => !f.is_final && f.stopped > 0)
    .reduce((a, f) => (f.stopped > (a?.stopped ?? -1) ? f : a), null as (typeof funnel)[number] | null)

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold text-2xl">Where booking attempts stop</h3>
      {/* The one line that changes what you DO stays visible; every rule for
          reading the bars is behind the icon — this card was carrying more prose
          than chart. */}
      <p className="text-lg text-muted-foreground mt-1">
        The longest bar is where to look first — click it for names.{" "}
        <InfoHint label="How to read these bars">
          <ul className="space-y-1 list-disc pl-4">
            <li>
              Every attempt counted <strong>once</strong>, at the furthest point it
              got, so the bars add up to {total}
              {outside > 0 ? `, plus ${outside} accounted for under the bars` : ""}.
            </li>
            <li>
              <strong>Attempts, not people</strong> — one user can appear in several
              bars.
            </li>
            <li>
              <strong>Don't subtract one bar from the next.</strong> "Cancelled" and
              "failed" are endings, not depths, so there's no pass rate to read here.
            </li>
            <li>A washed-out tail is attempts with no user attached.</li>
          </ul>
        </InfoHint>
      </p>

      {/* The headline counts live as tiles, not in the prose: the prose is how to
       *  read the bars, the tiles are the numbers. Left-aligned and width-capped so
       *  they read as a block under the description, not a full-width header row. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 mb-4 lg:max-w-4xl">
        <ScoreCard
          title="Booking attempts"
          value={total}
          icon={Activity}
          subtitle={`in the ${windowLabel(b.window_days)}`}
        />
        <ScoreCard
          title="Users who tried"
          value={b.unique_members ?? 0}
          icon={Users}
          subtitle={`made those ${total} attempts`}
        />
        <ScoreCard
          title="Ended with a class booked"
          value={booked}
          icon={CheckCircle}
          variant={booked > 0 ? "success" : "warning"}
          subtitle={
            total > 0
              ? `${Math.round((booked / total) * 100)}% of attempts got there`
              : "no attempts in this window"
          }
        />
        {worst && (
          <ScoreCard
            title="Biggest stopping point"
            value={worst.stopped}
            icon={AlertTriangle}
            variant="danger"
            subtitle={`${worst.label} · ${Math.round(
              (worst.stopped / Math.max(1, total)) * 100,
            )}% of attempts${
              worst.people.length > 0 ? ` · ${worst.people.length} users we can name` : ""
            }`}
          />
        )}
      </div>

      <div className="space-y-3">
        {funnel.map((f) => (
          <NamedBar
            key={f.stage}
            label={f.label}
            value={f.stopped}
            faded={f.anonymous}
            total={Math.max(1, total)}
            color={
              f.is_final
                ? "#8b5cf6"
                : f.stopped === 0
                  ? "#10b981"
                  : f.stage === "needs_user"
                    ? "#f59e0b"
                    : "#ef4444"
            }
            caption={
              f.is_final
                ? "class booked"
                : f.stopped === 0
                  ? "nothing stopped here"
                  : `stopped here${f.people.length > 0 ? ` · ${f.people.length} users` : " · none we can name"}`
            }
            sub={
              f.detail.length > 0 ? (
                <>
                  {f.detail_noun}:{" "}
                  {f.detail.map((d) => `${d.label} (${d.count})`).join(" · ")}
                </>
              ) : f.studios.length > 0 ? (
                <>mostly {f.studios.map((s) => `${s.name} (${s.count})`).join(" · ")}</>
              ) : undefined
            }
            hover={
              <>
                <div className="text-sm font-semibold mb-2">{f.label}</div>
                <div className="flex gap-4">
                  <NameList
                    title={f.is_final ? "Booked a class" : "Stopped here"}
                    names={f.people}
                    tone={f.is_final ? "good" : "bad"}
                  />
                  {f.studios.length > 0 && (
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold uppercase tracking-wide mb-1 text-muted-foreground">
                        Studios
                      </div>
                      <ul className="space-y-0.5">
                        {f.studios.map((s) => (
                          <li key={s.name} className="text-sm leading-tight truncate">
                            {s.name} · {s.count}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {f.anonymous > 0 && (
                  <p className="text-base text-muted-foreground mt-2 pt-2 border-t">
                    + {f.anonymous} attempts here we can't put a name to
                    {f.unnamed_why && (
                      <>
                        {": "}
                        {[
                          f.unnamed_why.deleted_account > 0 &&
                            `${f.unnamed_why.deleted_account} from accounts that no longer exist`,
                          f.unnamed_why.signed_out_device > 0 &&
                            `${f.unnamed_why.signed_out_device} from a signed-out device`,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </>
                    )}
                    .
                  </p>
                )}
              </>
            }
          />
        ))}
      </div>
      {((b.unstaged_attempts ?? 0) > 0 ||
        (b.untracked_events ?? 0) > 0 ||
        (b.test_attempts ?? 0) > 0 ||
        (b.other_cohort_attempts ?? 0) > 0 ||
        (b.unattributed_attempts ?? 0) > 0) && (
        <div className="text-base text-muted-foreground mt-3 pt-2 border-t space-y-1">
          {(b.test_attempts ?? 0) > 0 && (
            <p>
              <strong>
                Our own testing: {b.test_attempts} attempts{" "}
                {b.test_included ? "included above" : "left out"}.
              </strong>{" "}
              {b.staging_attempts} are tagged staging; the rest are dry runs from the
              spring build period or a test-named account.
            </p>
          )}
          {(b.other_cohort_attempts ?? 0) > 0 && (
            <p>
              <strong>On another tab: {b.other_cohort_attempts} attempts.</strong> Real
              attempts from outside{" "}
              {(b.cohorts?.length ?? 0) > 1
                ? `cohorts ${b.cohorts?.join(" and ")}`
                : `cohort ${b.cohorts?.[0]}`}
              — switch to All to see them in the bars.
            </p>
          )}
          {(b.unattributed_attempts ?? 0) > 0 && (
            <p>
              <strong>On no tab at all: {b.unattributed_attempts} attempts.</strong> No
              account behind them and no usable date, so no cohort can honestly claim them.
            </p>
          )}
          {(b.unstaged_attempts ?? 0) > 0 && (
            <p>
              <strong>Not in any bar above: {b.unstaged_attempts} attempts.</strong> Side
              events only — a deep link, a blocked duplicate — never a stage we track.
            </p>
          )}
          {(b.untracked_events ?? 0) > 0 && (
            <p>
              <strong>Older than the tracking: {b.untracked_events} events.</strong> Before
              10 Jun 2026 an event didn't say which attempt it belonged to. Reach back past
              that date and this card only covers the part after it.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function BookingDropoffSection({ b }: { b: BookingDropoff | null }) {
  if (!b || !b.configured || b.error) {
    return (
      <PlaceholderCard
        title="Booking Drop-off"
        need={
          b && !b.configured
            ? "Ready — set POSTHOG_PERSONAL_API_KEY + POSTHOG_PROJECT_ID on the backend."
            : "Couldn't load the booking drop-off this load."
        }
      />
    )
  }
  // Every number in this card is PostHog-sourced, so unlike Onboarding there is
  // no current half to reassure anyone about — if it's stale, all of it is.
  return (
    <div className="space-y-4">
      <StaleBanner asOf={b.stale_as_of} />
      <BookingDropoffCard b={b} />
    </div>
  )
}

/** Where the pilot start date lives between loads — per browser, the same way
 *  the Studios page remembers its column pick. Not worth a DB column yet. */
const PILOT_START_KEY = "offseason.dashboard.pilotStart"

/**
 * Studio pilot: how many DIFFERENT members got a class at each of the five
 * pilot studios. The ceiling on perk redemptions, not a count of them — see
 * the InfoHint: nothing books through the front desk yet, and the booking row
 * wouldn't record it if it did.
 *
 * The pilot is a fixed roster (Vital Ice, Body Temp Yoga, and the three X˙CORE
 * SF rooms), and the perk is the studio, not the plumbing: Offseason books by
 * logging into the studio's platform with front-desk credentials and pulling a
 * credit off a pre-purchased class pack, then bills the member the per-visit
 * rate. So a confirmed booking at a roster studio is a redemption whichever
 * rail placed it — the direct-API path is reported underneath as detail.
 *
 * All five are listed even at zero: "nobody has booked Body Temp yet" is an
 * answer, and dropping the row would look like missing data.
 *
 * With no pilot start set the card reads the dashboard's own window and costs
 * no extra request. Set the start and it refetches from that date, so classes
 * a member booked at a pilot studio BEFORE the pilot stop inflating its count.
 */
function StudioPerkCard({
  summary,
  cohorts,
}: {
  summary: BookingSummary | null
  cohorts: number[]
}) {
  const [start, setStart] = useState<string>(() => {
    try {
      return localStorage.getItem(PILOT_START_KEY) ?? ""
    } catch {
      return ""
    }
  })
  const [scoped, setScoped] = useState<BookingSummary | null>(null)
  const cohortKey = cohorts.join(",")

  useEffect(() => {
    try {
      if (start) localStorage.setItem(PILOT_START_KEY, start)
      else localStorage.removeItem(PILOT_START_KEY)
    } catch {
      /* storage full/blocked */
    }
    const from = Date.parse(start)
    if (!start || Number.isNaN(from)) {
      setScoped(null)
      return
    }
    // The endpoint windows by day count, not by date, so the picked date turns
    // into "days back from today" — clamped to the endpoint's own 1..365.
    const days = Math.min(
      MAX_WINDOW_DAYS,
      Math.max(1, Math.ceil((Date.now() - from) / 86_400_000)),
    )
    let cancelled = false
    getBookingSummary(days, cohortKey ? cohortKey.split(",").map(Number) : [])
      .then((s) => !cancelled && setScoped(s))
      .catch(() => !cancelled && setScoped(null))
    return () => {
      cancelled = true
    }
  }, [start, cohortKey])

  const source = start ? scoped : summary
  const pilot = source?.pilot
  const rows = pilot?.studios ?? []
  const viaApi = rows.reduce((n, s) => n + s.direct_api_confirmed, 0)

  return (
    <ChartCard
      title="Members booking at pilot studios"
      subtitle={
        <>
          {start ? `since ${start}` : windowLabel(summary?.window_days)} · the
          five pilot studios{" "}
          <InfoHint label="What counts here">
            A member counts once per studio the first time a booking there
            confirms, however it was placed — the pilot books by front-desk
            login against a pre-purchased class pack, so the browser route is
            the perk, not a workaround. Attempts that never confirmed don't
            count. Set a pilot start to cut off classes booked there beforehand.
            <br />
            <br />
            The roster is fixed in the backend (PILOT_STUDIO_IDS): Vital Ice on
            Mindbody, Body Temp Yoga and the three X˙CORE rooms on Mariana Tek.
            Other studios with a booking platform configured are not in the
            pilot and are not counted.
            <br />
            <br />
            This is the CEILING on perk redemptions, not a count of them. The
            front-desk login the PRD describes isn't built yet — the only
            studio logins we store are the member's own — so a class booked
            here today went through the member's own account at the studio's
            own price. And once the front-desk flow ships, the booking row
            still won't say which login placed it. To make this exact, stamp
            confirmation_data.booking_path = "front_desk_pack" when the agent
            draws a pack credit, and this tile filters on it.
          </InfoHint>
        </>
      }
    >
      <label className="flex items-center gap-2 text-base text-muted-foreground mb-2">
        Pilot start
        <input
          type="date"
          value={start}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setStart(e.target.value)}
          className="rounded border bg-background px-1.5 py-0.5 text-base"
        />
        {start && (
          <button
            onClick={() => setStart("")}
            className="underline hover:text-foreground"
          >
            clear
          </button>
        )}
      </label>
      {rows.length > 0 ? (
        <>
          <div className="space-y-1.5 py-1">
            {rows.map((s) => (
              <div
                key={s.studio_id}
                className="flex items-start justify-between gap-3 text-base"
              >
                <span className="truncate">
                  {s.studio_name}
                  <span className="ml-1.5 text-sm text-muted-foreground">
                    {s.platform ?? "no platform"}
                  </span>
                </span>
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {s.unique_users} {s.unique_users === 1 ? "member" : "members"}{" "}
                  · {s.confirmed} classes
                </span>
              </div>
            ))}
          </div>
          <p className="text-base text-muted-foreground pt-2 border-t mt-2">
            {pilot?.unique_users ?? 0} distinct{" "}
            {pilot?.unique_users === 1 ? "member" : "members"} across the pilot{" "}
            <InfoHint label="Why this isn't the column added up">
              Someone who booked at two pilot studios counts once here and once
              in each of their rows, so the rows sum higher than this number.
            </InfoHint>
          </p>
        </>
      ) : (
        <p className="text-base text-muted-foreground py-6 text-center">
          {source
            ? "No pilot studios configured."
            : "Couldn't load the booking summary this load."}
        </p>
      )}
      {viaApi > 0 && (
        <p className="text-base text-muted-foreground pt-1">
          {viaApi} of these went through the direct booking API rather than the
          front-desk login.
        </p>
      )}
    </ChartCard>
  )
}

/** Colour per completion status. Grey for unreported on purpose — it's an
 *  absence of information, not a member's failure. */
const WORKOUT_STATUS_STYLE: { key: keyof WorkoutCompletionMetrics["status_counts"]; label: string; color: string }[] = [
  { key: "complete", label: "Done in full", color: "#10b981" },
  { key: "partial", label: "Partly done", color: "#3b82f6" },
  { key: "missed", label: "Told us they skipped", color: "#ef4444" },
  { key: "unreported", label: "Never told us", color: "#cbd5e1" },
]

function WorkoutsCompletedSection({ w }: { w: WorkoutCompletionMetrics | null }) {
  if (!w) {
    return (
      <PlaceholderCard
        title="Workouts Generated + Completed"
        need="Couldn't load workout completion metrics this load (DB-sourced from the seasons table)."
      />
    )
  }

  const past = w.past_workout_days
  const logged = w.status_counts.complete + w.status_counts.partial + w.status_counts.missed
  const maxType = Math.max(1, ...w.types.map((t) => t.sessions))
  // Sum the drawn statuses directly so the bars always total 100% — rest,
  // untrackable and still-to-come days simply aren't in here.
  const scored = Math.max(1, logged + w.status_counts.unreported)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ScoreCard
          title="Workout days generated"
          value={w.workout_days}
          icon={Calendar}
          subtitle={`Across ${w.seasons} plans · ${w.rest_days} rest days on top`}
        />
        <ScoreCard
          title="Already happened"
          value={past}
          icon={Activity}
          subtitle={`${w.upcoming_workout_days} still ahead of them`}
        />
        <ScoreCard
          title="Users told us anything"
          value={w.logged_rate != null ? `${w.logged_rate}%` : "—"}
          icon={CheckCircle}
          variant={w.logged_rate != null && w.logged_rate < 25 ? "warning" : "default"}
          subtitle={`${logged} of ${past} past days carry any flag at all`}
        />
        <ScoreCard
          title="Done in full, of those logged"
          value={w.completion_rate != null ? `${w.completion_rate}%` : "—"}
          icon={CheckCircle}
          subtitle={`${w.status_counts.complete} of the ${logged} we heard about`}
        />
      </div>

      {/* Kept on screen rather than tucked into a tooltip: it no longer explains a
          gap in the product, it names one. Both ways to log a day shipped — the
          tick in the app (2026-07-13) and the coach asking in chat — and almost
          nobody uses either, which is a finding, not a footnote. */}
      <div className="rounded-lg border bg-amber-50/60 border-amber-200 p-3 text-sm text-amber-900">
        <strong>Read completion through the logging rate.</strong> The app doesn't ask
        users to tick a day off yet, so {w.status_counts.unreported} past days carry no
        flag. They show as <strong>"never told us"</strong>, never as missed. "Done in
        full" is a share of the slice we heard about, not of the whole.
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-card p-4">
          <h3 className="font-semibold text-2xl">What happened to each workout day</h3>
          <p className="text-base text-muted-foreground mt-0.5 mb-3">
            Every past workout day we can score — {scored} days.{" "}
            <InfoHint label="What's counted here">
              Counted <strong>once</strong> each, so the bars add to 100%. Rest days
              and days still ahead aren't in here
              {w.untrackable_days > 0 && `, nor are ${w.untrackable_days} days with nothing on them`}.
            </InfoHint>
          </p>
          <div className="space-y-2">
            {WORKOUT_STATUS_STYLE.map((s) => {
              const n = w.status_counts[s.key]
              return (
                <div key={s.key}>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-sm font-medium w-44 shrink-0 truncate">{s.label}</span>
                    <span className="text-sm font-semibold tabular-nums">{n}</span>
                    <span className="ml-auto text-base text-muted-foreground">
                      {Math.round((n / scored) * 100)}%
                    </span>
                  </div>
                  <div className="h-5 rounded bg-muted/50 overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.max((n / scored) * 100, n > 0 ? 1.5 : 0)}%`,
                        background: s.color,
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <h3 className="font-semibold text-2xl">What kind of training we prescribed</h3>
          <p className="text-base text-muted-foreground mt-0.5 mb-3">
            Per <strong>session</strong>, not per day.{" "}
            <InfoHint label="Why these add up to more than the day count">
              One day can hold a run and a lift, which is why these add up to more
              than {w.workout_days}.
            </InfoHint>
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 pb-2 border-b text-lg">
            {WORKOUT_STATUS_STYLE.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 font-medium">
                <span className="size-3 rounded-sm" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
          <div className="space-y-2.5">
            {w.types.map((t) => {
              const heard = t.complete + t.partial + t.missed
              return (
                <div key={t.type}>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-sm font-medium w-28 shrink-0 truncate">{t.label}</span>
                    <span className="text-sm font-semibold tabular-nums">{t.sessions}</span>
                    <span className="text-base text-muted-foreground truncate">
                      {t.type === "rest"
                        ? "nothing to complete"
                        : heard > 0
                          ? `${t.complete} done in full of the ${heard} we heard about`
                          : "nobody has logged one yet"}
                    </span>
                  </div>
                  <div className="h-5 rounded bg-muted/50 overflow-hidden flex">
                    {(
                      // Rest is never scored, so its status columns don't add up
                      // to its session count — draw it as one neutral bar rather
                      // than a stack that visually reads as a smaller number.
                      t.type === "rest"
                        ? ([[t.sessions, "#94a3b8"]] as [number, string][])
                        : ([
                            [t.complete, "#10b981"],
                            [t.partial, "#3b82f6"],
                            [t.missed, "#ef4444"],
                            [t.unreported, "#cbd5e1"],
                          ] as [number, string][])
                    ).map(([n, color], i) =>
                      n > 0 ? (
                        <div
                          key={i}
                          className="h-full"
                          style={{ width: `${(n / maxType) * 100}%`, background: color }}
                          title={`${n}`}
                        />
                      ) : null,
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {w.untrackable_days > 0 && (
            <p className="text-base text-muted-foreground mt-2">
              {w.untrackable_days} generated days carry nothing a user could tick off{" "}
              <InfoHint label="What those days look like">
                No exercises, no duration, no distance. Usually a class or a sport.
                Worth a look if it grows.
              </InfoHint>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/** The Users row. Headline is people who finished onboarding. RevenueCat
 *  supplies a fresher paying/trial split for the note when we can reach it —
 *  it must not replace the onboarded count, or a free walkthrough (Alyson,
 *  Quinn) vanishes from the number they were trying to inspect. */
function payingUsers(
  m: ExecMetric | undefined,
  rc: RcOverview | null,
  meta: CohortsResponse | null,
  picked: number[],
): { m: ExecMetric | undefined; value?: number | null; note?: string; suffix?: string } {
  if (!m) return { m }
  const onboarded = m.onboarded ?? m.value
  if (!rc) {
    return { m, value: onboarded, suffix: m.founding ? `${m.founding} founding` : undefined }
  }
  const external = (rc.real_purchasers ?? []).filter((p) => !p.is_internal)
  const scoped = external.filter((p) => inCohorts(meta, picked, p.id))
  const paying = scoped.filter((p) => p.paid_products.length > 0).length
  const trialing = scoped.filter((p) => p.state === "trialing").length
  const orphans = scoped.filter((p) => !p.has_profile).length
  const unplaced = picked.length
    ? external.filter((p) => !p.has_profile).length
    : 0
  // Paying = the card was actually charged (a live non-trial subscription).
  // Trial = signed up and inside the free trial, so nothing has been billed
  // yet. Selecting a plan lands you in Trial, never straight in Paying.
  const note =
    `Paying: ${paying} · Trial: ${trialing}` +
    (orphans ? ` · ${orphans} paying with no account row` : "") +
    (unplaced ? ` · ${unplaced} paying with no account row, so in no cohort` : "")
  return {
    m,
    value: onboarded,
    note,
    suffix: m.founding ? `${m.founding} founding` : undefined,
  }
}

/**
 * One line of the executive list: name on the left, the number on the right.
 * No week-over-week move — each cohort tab reads as a standing count.
 */
function ExecMetricRow({
  m,
  value: valueOverride,
  note: noteOverride,
  suffix,
}: {
  m: ExecMetric | undefined
  /** Replaces the backend value when a fresher source (RevenueCat) is loaded. */
  value?: number | null
  note?: string
  /** Rides next to the number for a share of it worth reading at a glance. */
  suffix?: string
}) {
  // A metric the backend stopped sending drops its own row instead of taking
  // the whole page down. This is what blanked prod on Aug 27: the deployed
  // bundle was two days behind a backend that had dropped two exec metrics.
  if (!m) return null
  const value = valueOverride === undefined ? m.value : valueOverride
  const isTime = m.unit === "s"
  const fmt = (n: number) => (isTime ? `${Math.round(n)}s` : n.toLocaleString())
  const shown = value == null ? "—" : fmt(value)

  return (
    <div className="flex items-baseline justify-between gap-4 py-4">
      <span className="text-base lg:text-lg font-semibold text-foreground">
        {m.label}
        {(noteOverride ?? m.note) ? (
          <span className="block text-sm font-normal text-muted-foreground">
            {noteOverride ?? m.note}
          </span>
        ) : null}
      </span>
      <span className="flex items-baseline gap-3 whitespace-nowrap">
        <span className="text-2xl lg:text-3xl font-semibold tabular-nums">{shown}</span>
        {suffix ? (
          <span className="text-base font-normal text-muted-foreground">({suffix})</span>
        ) : null}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// KPI strip
//
// Five numbers, at the top, larger than anything under them — the ones you
// would want if you could only have five. Three are RATES rather than counts,
// because a count at our size mostly measures how long we have been open: 65
// users is only good news against the 92 accounts it came from.
//
// Every tile shows its trend AND the span that trend covers, because a rising
// line with no dates on it says only "up", which is the least useful true
// sentence about a business. The number under the line is the whole move
// across that span — start to finish — so the shape and the size of the change
// are readable in the same glance.
//
// Weekly and daily actives sit side by side on purpose. They answer the same
// question at two speeds, and the gap between them IS the finding: a WAU far
// above DAU means people come back weekly but not daily. Daily actives opens
// on click, because one day's number is noise and the day-by-day history
// behind it is the part actually worth reading.
//
// WHAT THE LINES ARE MADE OF, since two different reconstructions are in play:
//   * Activation is exact. Accounts and finished onboardings are append-only
//     facts with their own timestamps, so any past day can be counted properly.
//   * Live plan % and paying % are reconstructions with a known lean (see the
//     backend's `trend` note): a re-extended plan reads as live on the days it
//     had actually lapsed, and churn is invisible because a member carries no
//     subscription timestamp. Both flatter the past. Read the direction, not
//     the pixel.
//   * WAU and DAU are PostHog, in-app sessions only, and go blank rather than
//     zero when PostHog is down — a zero here would read as "nobody opened the
//     app", which is a different and much worse sentence than "we couldn't
//     look".
// ---------------------------------------------------------------------------

/** "Aug 3" — short enough to sit under a sparkline without wrapping. */
function shortDate(iso: string): string {
  const [y, m, d] = String(iso).split("-").map(Number)
  if (!y || !m || !d) return String(iso)
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

/** One day on the actives chart, and the people who made it. */
type DayRow = { date: string; users: number; names?: string[] }

/**
 * PostHog emits NO row for a day nobody opened the app. Plotted raw, a dead
 * week becomes a straight line between the days on either side of it — the
 * chart quietly redraws a drought as steady traffic. Fill the gaps with the
 * zeros they actually are.
 */
function fillZeroDays(rows: DayRow[]): DayRow[] {
  if (rows.length < 2) return rows
  const DAY = 86_400_000
  const at = (s: string) => Date.parse(`${s}T00:00:00Z`)
  const first = at(rows[0].date)
  const last = at(rows[rows.length - 1].date)
  if (Number.isNaN(first) || Number.isNaN(last) || last < first) return rows
  const byDate = new Map(rows.map((r) => [r.date, r]))
  const out: DayRow[] = []
  for (let t = first; t <= last; t += DAY) {
    const iso = new Date(t).toISOString().slice(0, 10)
    // A filled day had nobody in it, so it gets nobody's name — an empty
    // roster, not an absent one, so clicking it says "nobody" out loud.
    out.push(byDate.get(iso) ?? { date: iso, users: 0, names: [] })
  }
  return out
}

/** A trend line with no axes, no grid and no numbers — shape only. */
function Spark({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return <div className="h-12" />
  const min = Math.min(...data)
  const max = Math.max(...data)
  // A flat series would collapse the axis onto itself, so a run of identical
  // days still gets room to draw a straight line through the middle.
  const pad = (max - min || Math.max(max, 1)) * 0.15
  return (
    <div className="h-12">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data.map((v, i) => ({ i, v }))}
          margin={{ top: 4, right: 2, left: 2, bottom: 2 }}
        >
          <YAxis hide domain={[min - pad, max + pad]} />
          <Line
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function KpiTile({
  label,
  value,
  caption,
  series,
  dates,
  unit = "pts",
  color,
  onClick,
  open,
}: {
  label: string
  /** Pre-formatted, or "—" when the source could not be read. */
  value: string
  caption: string
  series: number[]
  /** ISO days, one per point in `series` — what the line is dated by. */
  dates: string[]
  /** How to name the move across the span. Percentages move in points. */
  unit?: string
  color: string
  onClick?: () => void
  open?: boolean
}) {
  // The move across the whole span, not day over day: the question a sparkline
  // is being asked is "where has this got to since then", and "then" is the
  // left edge of the line the reader is looking at.
  const move = series.length >= 2 ? series[series.length - 1] - series[0] : null
  const flat = move != null && Math.abs(move) < 0.5
  const moveLabel =
    move == null
      ? ""
      : flat
        ? "no change"
        : `${move > 0 ? "+" : "−"}${Math.round(Math.abs(move)).toLocaleString()}${unit ? ` ${unit}` : ""}`

  const body = (
    <>
      <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
        {onClick && (
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </div>
      <div className="mt-2 text-4xl font-semibold tabular-nums leading-none">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
      <div className="mt-3">
        <Spark data={series} color={color} />
      </div>
      {dates.length >= 2 && (
        <div className="mt-1.5 flex items-baseline justify-between gap-2 text-[11px] tabular-nums text-muted-foreground">
          <span>{shortDate(dates[0])}</span>
          <span
            className={
              flat
                ? "font-medium"
                : move != null && move > 0
                  ? "font-medium text-emerald-600"
                  : "font-medium text-rose-600"
            }
          >
            {moveLabel}
          </span>
          <span>{shortDate(dates[dates.length - 1])}</span>
        </div>
      )}
    </>
  )

  const shell = "rounded-xl border bg-card p-5 shadow-xs"
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!!open}
      className={`${shell} w-full text-left transition-colors hover:border-foreground/30 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring`}
    >
      {body}
    </button>
  ) : (
    <div className={shell}>{body}</div>
  )
}

/** The day-by-day history behind the Daily actives tile. */
function DailyActivesPanel({ rows }: { rows: DayRow[] }) {
  // Which day's roster is open. Null until someone clicks the line — the
  // chart's job is still the shape; the names are the follow-up question.
  const [pickedDate, setPickedDate] = useState<string | null>(null)
  const picked = pickedDate ? rows.find((r) => r.date === pickedDate) ?? null : null
  const last7 = rows.slice(-7)
  const avg7 = last7.length ? last7.reduce((s, r) => s + r.users, 0) / last7.length : 0
  const best = rows.reduce((b, r) => (r.users > b.users ? r : b), rows[0])

  return (
    <div className="mt-4 rounded-xl border bg-card p-5 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="text-sm font-medium">People in the app, day by day</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {shortDate(rows[0].date)} – {shortDate(rows[rows.length - 1].date)} · in-app
            sessions only, and a day nobody opened the app is drawn as the zero it was
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Click a day to see who was in the app.
          </div>
        </div>
        <div className="flex gap-8">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">7-day avg</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums leading-none">
              {avg7.toFixed(1)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Busiest day</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums leading-none">
              {best.users.toLocaleString()}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{shortDate(best.date)}</div>
          </div>
        </div>
      </div>
      <div className="mt-5 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={rows}
            margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
            // Recharts hands back the day under the cursor as `activeLabel`;
            // clicking the open day again closes it, so the chart is never
            // stuck showing a roster nobody asked for a second time.
            onClick={(e: { activeLabel?: string | number }) => {
              const d = e?.activeLabel == null ? null : String(e.activeLabel)
              setPickedDate((cur) => (d && d !== cur ? d : null))
            }}
            style={{ cursor: "pointer" }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={shortDate}
              minTickGap={40}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip
              labelFormatter={(d) => shortDate(String(d))}
              formatter={(v) => [String(v ?? 0), "people"]}
            />
            {picked && <ReferenceLine x={picked.date} stroke="#8b5cf6" strokeDasharray="4 3" />}
            <Line
              type="monotone"
              dataKey="users"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {picked && (
        <div className="mt-4 border-t pt-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium">
                {shortDate(picked.date)} · {picked.users.toLocaleString()}{" "}
                {picked.users === 1 ? "person" : "people"}
              </div>
              {/* The count comes from the query; the names come from the same
               *  ids that count was taken over, capped at 200. Say so when the
               *  cap bites rather than letting the list look short. */}
              {picked.users > (picked.names?.length ?? 0) && (
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Showing the first {picked.names?.length ?? 0} — the rest are over the cap.
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPickedDate(null)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
          {picked.names?.length ? (
            <div className="mt-3">
              <NameList title="In the app" names={picked.names} tone="good" />
            </div>
          ) : (
            <div className="mt-2 text-sm text-muted-foreground">
              {picked.users > 0
                ? "Nobody on this day could be matched back to an account."
                : "Nobody opened the app on this day."}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function KpiStrip({
  exec,
  general,
}: {
  exec: ExecutiveSummary | null
  general: GeneralMetrics | null
}) {
  const [openDaily, setOpenDaily] = useState(false)
  const t = exec?.trend ?? []
  const pct = (n: number | null | undefined, d: number | null | undefined) =>
    d ? `${Math.round((100 * (n ?? 0)) / d)}%` : "—"
  // Days with no denominator are dropped, so the dates have to be dropped with
  // them or the line and its date range stop describing the same points.
  const share = (rows: { date: string; n: number; d: number }[]) => {
    const ok = rows.filter((r) => r.d > 0)
    return { series: ok.map((r) => (100 * r.n) / r.d), dates: ok.map((r) => r.date) }
  }

  const accounts = exec?.metrics.total_accounts.accounts ?? null
  const users = exec?.metrics.total_accounts.users ?? null
  const livePlans = exec?.metrics.active_seasons.value ?? null
  const subscribed =
    exec == null
      ? null
      : (exec.metrics.paying_users.paying ?? 0) + (exec.metrics.paying_users.trialing ?? 0)

  // PostHog rows only exist for days somebody opened the app, and the first six
  // days of any window have less than a week behind them, so `wau` only reads
  // true from the seventh row on. Blank beats a number we know is short.
  const daily = general?.daily ?? []
  const wauRows = daily.slice(6)
  const wauNow = wauRows.length ? (wauRows[wauRows.length - 1].wau ?? null) : null

  const dauRows = fillZeroDays(
    daily.map((d) => ({ date: d.date, users: d.users, names: d.names })),
  )
  // Today's number, not an average of the week — the tile answers "who is in
  // the app right now" and the line under it carries the context. PostHog buckets
  // by its own project day, which can already have rolled over ahead of us
  // locally, so the newest row counts as today whenever it is not in the past.
  const todayIso = new Date().toLocaleDateString("en-CA")
  const newest = dauRows.length ? dauRows[dauRows.length - 1] : null
  const dauToday = newest ? (newest.date >= todayIso ? newest.users : 0) : null

  return (
    <section className="mb-10">
      {/* The two raw counts the strip only ever shows as a percentage. Asked
          for back on top because a rate with no denominator under it is a
          number you can't sanity-check. */}
      <div className="mb-3 flex flex-wrap items-baseline gap-x-10 gap-y-1">
        <span className="text-lg text-muted-foreground">
          Users:{" "}
          <strong className="text-2xl font-semibold text-foreground tabular-nums">
            {users ?? "—"}
          </strong>
        </span>
        <span className="text-lg text-muted-foreground">
          Active Seasons:{" "}
          <strong className="text-2xl font-semibold text-foreground tabular-nums">
            {livePlans ?? "—"}
          </strong>
          <InfoHint label="What counts as an active season">
            A user whose plan still has days ahead of them. It's the same fact
            as “Has a live plan” below, before it's turned into a share.
          </InfoHint>
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiTile
          label="Activation"
          value={pct(users, accounts)}
          caption={
            users != null && accounts != null
              ? `${users} of ${accounts} accounts finished onboarding`
              : "Accounts that became users"
          }
          {...share(t.map((d) => ({ date: d.date, n: d.users, d: d.accounts })))}
          color="#6366f1"
        />
        <KpiTile
          label="Weekly actives"
          value={wauNow == null ? "—" : wauNow.toLocaleString()}
          caption={
            wauNow == null
              ? "App activity unavailable this load"
              : `Different people, last 7 days${
                  users ? ` · ${Math.round((100 * wauNow) / users)}% of users` : ""
                }`
          }
          series={wauRows.map((d) => d.wau ?? 0)}
          dates={wauRows.map((d) => d.date)}
          unit=""
          color="#10b981"
        />
        <KpiTile
          label="Daily actives"
          value={dauToday == null ? "—" : dauToday.toLocaleString()}
          caption={
            dauToday == null ? "App activity unavailable this load" : "In the app today"
          }
          series={dauRows.map((d) => d.users)}
          dates={dauRows.map((d) => d.date)}
          unit=""
          color="#8b5cf6"
          onClick={dauRows.length ? () => setOpenDaily((v) => !v) : undefined}
          open={openDaily}
        />
        <KpiTile
          label="Has a live plan"
          value={pct(livePlans, users)}
          caption={
            livePlans != null && users != null
              ? `${livePlans} of ${users} users have workouts ahead of them`
              : "Users with workouts still ahead"
          }
          {...share(t.map((d) => ({ date: d.date, n: d.live_plans, d: d.users })))}
          color="#3b82f6"
        />
        <KpiTile
          label="Paying or trialing"
          value={pct(subscribed, users)}
          caption={
            subscribed != null && users != null
              ? `${exec?.metrics.paying_users.paying ?? 0} paying · ${
                  exec?.metrics.paying_users.trialing ?? 0
                } on trial`
              : "Users on a paid plan or a trial"
          }
          {...share(t.map((d) => ({ date: d.date, n: d.paying, d: d.users })))}
          color="#f59e0b"
        />
      </div>
      {openDaily && dauRows.length > 0 && <DailyActivesPanel rows={dauRows} />}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Cohorts
//
// A cohort is a group of PEOPLE, not a slice of the calendar: it is the batch
// that arrived together, and you stay in the one you arrived with. That is the
// only version of the split worth comparing — if cohort 2 meant "everything
// after the boundary", cohort 1 members carrying on with the app would fill it,
// and the two bars would say nothing about whether the new batch onboards any
// better.
//
// The clock is the day someone SIGNED UP, not the day they first appeared on the
// waitlist: a waitlist row can predate the account by months, so filing by first
// appearance put the whole unconverted waitlist in cohort 1 — a thousand people
// against fifty, most of whom never opened the app. For the roster rows that
// never became an account, approval is the only arrival date they carry and the
// backend falls back to it.
//
// Most cards are filtered on the server (`?cohorts=1,2`). The few the browser
// computes itself — AI activity, season health, the signup and access funnels,
// the recent-activity list — are filtered here from the SAME assignment the
// server used, so two cards on one screen can never file the same member twice.
// ---------------------------------------------------------------------------

/** Which cohort an ARRIVAL timestamp falls in. Half-open, matching the backend. */
function cohortOf(windows: Cohort[], ts?: string | null): number | null {
  if (!ts) return null
  const t = Date.parse(ts)
  if (Number.isNaN(t)) return null
  for (const w of windows) {
    const start = w.start ? Date.parse(w.start) : -Infinity
    const end = w.end ? Date.parse(w.end) : Infinity
    if (t >= start && t < end) return w.n
  }
  return null
}

/**
 * Is this person in the picked cohorts? By their member id when they have one,
 * otherwise by `approvedAt` — someone we let in who never made an account has no
 * profile for the server to have filed, so the day we approved them is the only
 * arrival date they carry.
 *
 * Anyone WITH an account goes through `assignments`, which the server fills from
 * their signup day. The fallback is for the no-account half of the roster only;
 * do not reach for it to file a member, or they will land in a different cohort
 * than every card beside them.
 *
 * An empty pick means every cohort — but NOT every account. Our own dry runs
 * come out on every tab, because that is what the server-filtered cards on the
 * same screen do: leaving them in was 2 QA accounts of daylight between the All
 * tab and every card beside it.
 */
function inCohorts(
  meta: CohortsResponse | null,
  picked: number[],
  id?: string | null,
  approvedAt?: string | null,
): boolean {
  if (!meta) return true
  if (id && meta.internal_ids?.includes(id)) return false
  if (!picked.length) return true
  const n =
    (id ? meta.assignments[id] : undefined) ?? cohortOf(meta.cohorts, approvedAt)
  return n != null && picked.includes(n)
}

/** The endpoints' own cap on `days`, and what "all time" means here. */
const MAX_WINDOW_DAYS = 365

/**
 * How far back the windowed cards read, from the cohort pick alone — a fixed
 * 30 days made "All time" a lie and clipped cohort 1 to its last month.
 *
 * Runs from the OLDEST picked cohort's start to today, never to that cohort's
 * end: a cohort bounds when people JOINED, and a cohort-1 member using the app
 * this morning is still cohort 1. Stopping at the boundary would show their
 * activity as having ceased the day the next cohort opened.
 */
function windowDays(meta: CohortsResponse | null, picked: number[]): number {
  if (!meta || !picked.length) return MAX_WINDOW_DAYS
  const starts = meta.cohorts.filter((c) => picked.includes(c.n)).map((c) => c.start)
  // Cohort 1 has no start — it reaches back to the beginning, so: all time.
  if (!starts.length || starts.some((s) => !s)) return MAX_WINDOW_DAYS
  const oldest = Math.min(...starts.map((s) => Date.parse(s as string)))
  if (Number.isNaN(oldest)) return MAX_WINDOW_DAYS
  const days = Math.ceil((Date.now() - oldest) / 86_400_000)
  return Math.min(MAX_WINDOW_DAYS, Math.max(1, days))
}

/** How a card should NAME its window, so subtitles stop saying "last 30 days". */
function windowLabel(days: number | null | undefined): string {
  if (days == null) return "all time"
  return days >= MAX_WINDOW_DAYS ? "all time" : `last ${days} days`
}

/** Multi-select cohort tabs. Nothing picked = All, which is also every tab on. */
function CohortTabs({
  meta,
  picked,
  onChange,
  compare,
  onCompare,
}: {
  meta: CohortsResponse | null
  picked: number[]
  onChange: (next: number[]) => void
  compare: boolean
  onCompare: (next: boolean) => void
}) {
  if (!meta || meta.cohorts.length < 2) return null
  // Change is a mode of this same control, so All must not read as lit under it.
  const all = picked.length === 0 && !compare

  function toggle(n: number) {
    const next = picked.includes(n) ? picked.filter((x) => x !== n) : [...picked, n]
    // Picking a single cohort is the filter, not the comparison — leave Change.
    onCompare(false)
    // "every tab on" and "no tab on" are the same view, so collapse to All and
    // keep one button lit rather than N.
    onChange(next.length === meta!.cohorts.length ? [] : next.sort((a, b) => a - b))
  }

  const pill =
    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors"

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-muted-foreground mr-1">Cohort</span>
      <button
        onClick={() => {
          onCompare(false)
          onChange([])
        }}
        className={cn(
          pill,
          all
            ? "border-primary bg-primary text-primary-foreground"
            : "bg-background hover:bg-muted",
        )}
        title="Every cohort together — the dashboard as it was before cohorts"
      >
        All
      </button>
      {meta.cohorts.map((c) => {
        const on = picked.includes(c.n)
        return (
          <button
            key={c.n}
            onClick={() => toggle(c.n)}
            className={cn(
              pill,
              on
                ? "border-primary bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted",
            )}
            // The number on the chip is ACCOUNTS, not the size of the batch —
            // it comes from `profiles`, so the people we approved who never
            // opened the app can't be in it. Say so, or it reads as the cohort
            // size and contradicts the "Approved" row two inches below.
            title={
              c.label +
              (c.members != null ? ` — ${c.members} of them made an account` : "") +
              (c.internal
                ? ` · ${c.internal} dry-run / test accounts here, not counted`
                : "")
            }
          >
            <span className="font-medium">{c.n}</span>
            <span className={on ? "opacity-90" : "text-muted-foreground"}>
              {c.label}
            </span>
            {c.members != null && (
              <span className={on ? "opacity-75" : "text-muted-foreground"}>
                · {c.members}
              </span>
            )}
          </button>
        )
      })}
      <button
        onClick={() => onCompare(!compare)}
        className={cn(
          pill,
          compare
            ? "border-primary bg-primary text-primary-foreground"
            : "bg-background hover:bg-muted",
        )}
        title="Every cohort side by side with the move between them, as rates rather than totals"
      >
        <ArrowRight className="size-3.5" />
        Change
      </button>
      {!all && !compare && (
        <span className="text-sm text-muted-foreground">
          Cron status and booking SLA belong to the system rather than to a
          user, so they read the same in every cohort.
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The Change tab
//
// The cohort pills FILTER: you read cohort 1, switch, read cohort 2, and hold
// the first set in your head. Change puts every cohort on one screen with the
// move between the last two.
//
// RATES, NOT TOTALS. Cohort 1 is most of the roster with months behind it; a
// newer batch is days old and a handful of people, so every raw total reads as
// a collapse while saying nothing about whether the new batch onboards, trains
// or books any better. Denominators stay on screen, because a share of five
// people is not a finding — and the two cumulative rows are shown WITHOUT a
// good/bad colour, since the older cohort has simply had more weeks to add to
// them.
//
// No new endpoint: /admin/executive_summary already takes ?cohorts=N, so this
// is the call the page already makes, once per cohort.
// ---------------------------------------------------------------------------

type ChangeCell = { num: number | null; den?: number | null }

type ChangeRow = {
  label: string
  hint: string
  /** How to read it: a share of a group, an average per member, a raw count, seconds. */
  kind: "share" | "per" | "count" | "seconds"
  cells: ChangeCell[]
  lowerIsBetter?: boolean
  /** Cumulative, so the older cohort wins on age alone — show it, don't score it. */
  ageSkewed?: boolean
  /** Why this row must not be scored, in its own words. Greys the change like ageSkewed. */
  caveat?: string
}

/** The one number a cell stands for: a share, an average, or the raw value. */
function changeValue(kind: ChangeRow["kind"], c: ChangeCell): number | null {
  if (c.num == null) return null
  if (kind === "count" || kind === "seconds") return c.num
  return c.den ? c.num / c.den : null
}

function fmtChangeCell(kind: ChangeRow["kind"], c: ChangeCell): string {
  const v = changeValue(kind, c)
  if (v == null) return "—"
  if (kind === "count") return v.toLocaleString()
  if (kind === "seconds") return `${Math.round(v)}s`
  if (kind === "share") return `${Math.round(v * 100)}%`
  // A per-member average under a tenth is real — one workout across 22 people is
  // 0.05, and printing that as "0.0" says nobody did anything.
  return v.toFixed(v !== 0 && Math.abs(v) < 0.1 ? 2 : 1)
}

/** The denominator, spelled out, so a percentage over three people can't hide. */
function fmtChangeBasis(kind: ChangeRow["kind"], c: ChangeCell): string | null {
  if (c.num == null || kind === "count" || kind === "seconds") return null
  if (!c.den) return null
  return kind === "share" ? `${c.num} of ${c.den}` : `${c.num} over ${c.den}`
}

/** Rows for every cohort, read off each one's own executive summary. */
function changeRows(execs: (ExecutiveSummary | null)[]): ChangeRow[] {
  const across = (read: (x: ExecutiveSummary["metrics"]) => ChangeCell): ChangeCell[] =>
    execs.map((e) => (e ? read(e.metrics) : { num: null }))
  // The headline "Users" number IS the onboarded count; `onboarded` is the
  // explicit field for it and the fallback keeps this honest if that changes.
  const onboarded = (x: ExecutiveSummary["metrics"]): number | null =>
    x.paying_users?.onboarded ?? x.paying_users?.value ?? null

  return [
    {
      // Cohort 1's window has no start, so this is "all of history" against a
      // window that is days old. It is the denominator for every row below it,
      // not a batch-vs-batch result — hence no colour.
      label: "People in the cohort",
      hint: "Users and waitlist alike, by first appearance. Cohort 1 reaches back to the beginning, so the sizes aren't comparable",
      kind: "count",
      caveat:
        "The size of a batch is a decision we made, not a result — this is the denominator for the rows below",
      cells: across((x) => ({ num: x.total_accounts?.value ?? null })),
    },
    {
      label: "Accounts",
      hint: "Made an in-app account, as a share of the people we approved in this batch",
      kind: "share",
      // Still not scoreable, but for a completely different reason than before:
      // the denominator is now clean (approved people only, no waitlist), and
      // what skews it is TIME. Cohort 1 has had months for its approvals to
      // trickle in and the newest batch has had days, so a lower share in the
      // new cohort may only mean its people have not got round to it yet.
      caveat:
        "The newest batch has had days to act on its invitations and the older one had months — this share climbs on its own as the batch ages",
      cells: across((x) =>
        x.total_accounts
          ? { num: x.total_accounts.accounts ?? 0, den: x.total_accounts.value }
          : { num: null },
      ),
    },
    {
      // Denominator is the accounts, not the whole batch — and this is the ONE
      // row on the strip that age does not skew, because onboarding happens
      // minutes after making an account, not weeks. It is where an onboarding
      // fix shows up first.
      label: "Users",
      hint: "Finished onboarding, as a share of the people who made an account. Onboarding happens minutes after signup, so unlike the rows above and below, this one is not skewed by how old the batch is",
      kind: "share",
      cells: across((x) => ({ num: onboarded(x), den: x.total_accounts?.accounts ?? null })),
    },
    {
      label: "Paying or trialing",
      hint: "Share of onboarded users on a paid plan or free trial. Comped founding members are in neither, and all sit in cohort 1",
      kind: "share",
      cells: across((x) =>
        x.paying_users
          ? {
              num: (x.paying_users.paying ?? 0) + (x.paying_users.trialing ?? 0),
              den: onboarded(x),
            }
          : { num: null },
      ),
    },
    {
      label: "Has a live plan",
      hint: "Share of onboarded users whose season still covers today",
      kind: "share",
      cells: across((x) => ({ num: x.active_seasons?.value ?? null, den: onboarded(x) })),
    },
    {
      label: "Workouts completed",
      hint: "Per onboarded user, all time — cumulative, so the older cohort leads on age alone",
      kind: "per",
      ageSkewed: true,
      cells: across((x) => ({ num: x.workouts_completed?.value ?? null, den: onboarded(x) })),
    },
    {
      label: "Studio bookings",
      hint: "Confirmed bookings per onboarded user, all time — cumulative, same age caveat",
      kind: "per",
      ageSkewed: true,
      cells: across((x) => ({ num: x.studio_bookings?.value ?? null, den: onboarded(x) })),
    },
    {
      label: "Booking speed",
      hint: "Prompt to confirmation, averaged over each cohort's own confirmed bookings",
      kind: "seconds",
      lowerIsBetter: true,
      cells: across((x) => ({ num: x.booking_speed?.value ?? null })),
    },
  ]
}

/** How many people a cell rests on — the guard against reading noise as a trend. */
function changeSample(r: ChangeRow, c: ChangeCell): number | null {
  if (r.kind === "seconds") return null
  return (r.kind === "count" ? c.num : c.den) ?? null
}

/** The move from the previous cohort to the newest one. */
function changeDelta(r: ChangeRow) {
  if (r.cells.length < 2) return null
  const prev = r.cells[r.cells.length - 2]
  const now = r.cells[r.cells.length - 1]
  const a = changeValue(r.kind, prev)
  const b = changeValue(r.kind, now)
  if (a == null || b == null) return null
  // Shares move in POINTS. "62% to 71%" is +9 points, not +15% — the percentage
  // of a percentage is the classic way to overstate a nine-point move.
  const pts = r.kind === "share" ? (b - a) * 100 : null
  const pct = pts == null && a !== 0 ? ((b - a) / a) * 100 : null
  const thin = [prev, now].some((c) => {
    const n = changeSample(r, c)
    return n != null && n < 5
  })
  return { pts, pct, up: b > a, flat: Math.abs(b - a) < 1e-9, thin }
}

/**
 * The verdict on a move, and the reason it might not be one.
 *
 * Pulled out of the table so the cohort strip under Volume and the full
 * Change tab share ONE implementation: a metric that reads "not a verdict" in
 * one place cannot read green in the other.
 */
function changeVerdict(r: ChangeRow) {
  const d = changeDelta(r)
  if (d == null) return { d: null, tone: "muted" as const, why: undefined, Arrow: Minus }
  const good = d.flat ? null : r.lowerIsBetter ? !d.up : d.up
  const why =
    r.caveat ??
    (r.ageSkewed
      ? "Cumulative — the older cohort has had more weeks to add to this, so the direction is not a verdict"
      : d.thin
        ? "Under five people on one side of this — one person changing their mind moves it"
        : undefined)
  // Grey means "don't score this": too few people for the move to mean
  // anything, or a cumulative row the older cohort wins on age.
  const unscored = d.thin || r.ageSkewed || !!r.caveat || good == null
  return {
    d,
    tone: unscored ? ("muted" as const) : good ? ("good" as const) : ("bad" as const),
    why,
    Arrow: d.flat ? Minus : d.up ? ArrowUp : ArrowDown,
  }
}

const CHANGE_TONE = {
  good: "text-emerald-600",
  bad: "text-red-600",
  muted: "text-muted-foreground",
} as const

/** The move itself, in words. Shares are POINTS, everything else percent. */
function fmtChangeDelta(d: NonNullable<ReturnType<typeof changeDelta>>): string {
  if (d.flat) return "same"
  if (d.pts != null)
    return `${d.pts > 0 ? "+" : "−"}${Math.abs(d.pts).toFixed(0)} ${Math.abs(d.pts) < 1.5 ? "pt" : "pts"}`
  if (d.pct != null) return `${d.pct > 0 ? "+" : "−"}${Math.abs(d.pct).toFixed(0)}%`
  return "new"
}

/** One block of the comparison: metrics down the side, cohorts across the top.
 *  Shared by all three, so a row always reads the same way wherever it sits. */
function ChangeTable({ meta, rows }: { meta: CohortsResponse; rows: ChangeRow[] }) {
  const last = meta.cohorts[meta.cohorts.length - 1]
  const prev = meta.cohorts[meta.cohorts.length - 2]
  return (
  <div className="rounded-lg border bg-card overflow-x-auto">
    <table className="w-full text-base">
      <thead>
        <tr className="border-b text-sm text-muted-foreground">
          <th className="px-6 py-3 text-left font-medium">Metric</th>
          {meta.cohorts.map((c) => (
            <th key={c.n} className="px-6 py-3 text-right font-medium whitespace-nowrap">
              Cohort {c.n}
              <span className="block text-sm font-normal">{c.label}</span>
            </th>
          ))}
          <th className="px-6 py-3 text-right font-medium whitespace-nowrap">
            Change
            {last && prev && (
              <span className="block text-sm font-normal">
                {last.n} vs {prev.n}
              </span>
            )}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {rows.map((r) => {
          const { d, tone, why, Arrow } = changeVerdict(r)
          return (
            <tr key={r.label} className="align-top">
              <td className="px-6 py-4">
                <span className="font-semibold text-foreground">{r.label}</span>{" "}
                <InfoHint label={`How “${r.label}” is measured`}>{r.hint}</InfoHint>
              </td>
              {r.cells.map((c, i) => (
                <td key={i} className="px-6 py-4 text-right whitespace-nowrap">
                  <span className="text-2xl font-semibold tabular-nums">
                    {fmtChangeCell(r.kind, c)}
                  </span>
                  {fmtChangeBasis(r.kind, c) && (
                    <span className="block text-sm text-muted-foreground tabular-nums">
                      {fmtChangeBasis(r.kind, c)}
                    </span>
                  )}
                </td>
              ))}
              <td className="px-6 py-4 text-right whitespace-nowrap">
                {d == null ? (
                  <span className="text-base text-muted-foreground/60">—</span>
                ) : (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-lg font-medium tabular-nums",
                      CHANGE_TONE[tone],
                    )}
                    title={why}
                  >
                    <Arrow className="size-4" />
                    {fmtChangeDelta(d)}
                  </span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  </div>
  )
}


/** A signup step, as the card reads it: whoever got at least this far, and how
 *  many of them went on. Same arithmetic as the drop-off card's own bars. */
type StepCell = { reached: number; completed: number }

function stepPass(c: StepCell | null): ChangeCell {
  return c ? { num: c.completed, den: c.reached } : { num: null }
}

/**
 * Signup steps, per cohort. Rows are joined by SCREEN, never by position — a
 * cohort that never reported a screen must leave a hole in that row rather than
 * shift every step below it up one.
 */
function funnelChangeRows(onbs: (OnboardingMetrics | null)[]): ChangeRow[] {
  const first = onbs.find((o) => o?.funnel?.length)?.funnel ?? []
  const steps = first.filter((f) => !f.is_final)
  if (!steps.length) return []

  const cell = (o: OnboardingMetrics | null, screen: string): StepCell | null => {
    const f = o?.funnel?.find((x) => x.screen === screen)
    if (!f || f.reached === 0) return null
    return { reached: f.reached, completed: f.is_final ? f.reached : f.reached - f.dropped }
  }

  const rows: ChangeRow[] = [
    {
      // The one number that answers "did signup get better" — everything below
      // says WHERE. Signup happens in one sitting, so a young cohort is not
      // penalised here the way the cumulative rows above are.
      label: "Landing to account",
      hint: "End to end: of everyone who opened signup, the share that finished with an account",
      kind: "share",
      cells: onbs.map((o) => {
        const open = o?.funnel?.[0]
        const done = o?.funnel?.find((f) => f.is_final)
        if (!open || !done || open.reached === 0) return { num: null }
        return { num: done.reached, den: open.reached }
      }),
    },
  ]
  for (const st of steps) {
    rows.push({
      label: st.label,
      hint: `Of everyone who reached ${st.label.toLowerCase()}, the share that went on`,
      kind: "share",
      cells: onbs.map((o) => stepPass(cell(o, st.screen))),
    })
  }
  return rows
}

/** The weakest step in one cohort: lowest pass rate, same >=10-reached floor the
 *  drop-off card uses, so the two cards can never name different steps. */
function weakestStep(o: OnboardingMetrics | null) {
  let worst: { label: string; reached: number; completed: number; pass: number } | null = null
  for (const f of o?.funnel ?? []) {
    if (f.is_final || f.reached < 10 || f.dropped <= 0) continue
    const completed = f.reached - f.dropped
    const pass = Math.round((completed / f.reached) * 100)
    if (!worst || pass < worst.pass) worst = { label: f.label, reached: f.reached, completed, pass }
  }
  return worst
}

/**
 * Getting in the door, per cohort. Every stage is dated by ITS OWN event —
 * application, approval, first account — so these do NOT chain within a cohort:
 * a batch approval files 100 people under the cohort of the batch while their
 * applications sit in an earlier one. Each row is read alone.
 */
function accessChangeRows(onbs: (OnboardingMetrics | null)[]): ChangeRow[] {
  const first = onbs.find((o) => o?.access?.stages?.length)?.access?.stages ?? []
  const stages = first.filter((s) => !s.is_final)
  // Named as the MOVE, not the stage, because the number is the share that left
  // this stage for the next one — a stage name on that row reads as its size.
  const label: Record<string, string> = {
    "Applied for access": "Applied → approved",
    "Approved for access": "Approved → started an account",
    "Started an account": "Started → finished onboarding",
  }
  const hint: Record<string, string> = {
    "Applied for access": "Share of applicants we approved. Dated by the approval, so a batch counts in the cohort we ran it in",
    "Approved for access": "Share of approvals that went on to start an account",
    "Started an account": "Share of started accounts that finished onboarding",
  }
  return stages.map((st) => ({
    label: label[st.label] ?? st.label,
    hint: hint[st.label] ?? "Share that went on to the next stage",
    kind: "share" as const,
    // A week-old approval batch has had no time to open the app; that is a clock,
    // not a result, so the move is shown without a verdict.
    caveat:
      st.label === "Approved for access"
        ? "Approvals need time to act. A low share on the newest batch is the clock, not the batch"
        : undefined,
    cells: onbs.map((o) => {
      const m = o?.access?.stages?.find((x) => x.label === st.label)
      if (!m || m.reached === 0) return { num: null }
      return { num: m.reached - m.dropped, den: m.reached }
    }),
  }))
}

/**
 * Are the incremental changes working? The newest group of people against the
 * one before it, on the same rows as the full Change tab.
 *
 * A cohort here is a group of PEOPLE, joined by when they first showed up — not
 * a calendar slice. The boundaries are OURS: we set them in
 * `ADMIN_COHORT_BOUNDARIES` when we ship something worth measuring, so cohort 2
 * is "the people who arrived after the change" and cohort 1 is "the people who
 * arrived before it". That is the whole point of comparing them, and it's why
 * these two columns never move when you switch the all-time / cohort tabs above:
 * the comparison is between fixed groups, so it has to stay fixed.
 *
 * Everything here comes from `changeRows` + `changeVerdict`, the same functions
 * the Change tab uses. A row that reads grey there cannot read green here.
 */
function CohortMoveStrip({
  meta,
  execs,
  onSeeAll,
}: {
  meta: CohortsResponse | null
  execs: (ExecutiveSummary | null)[] | null
  onSeeAll: () => void
}) {
  if (!meta || meta.cohorts.length < 2) return null

  const prev = meta.cohorts[meta.cohorts.length - 2]
  const last = meta.cohorts[meta.cohorts.length - 1]

  const header = (
    <div className="flex flex-wrap items-baseline justify-between gap-2 mt-6 mb-3">
      <div>
        <h3 className="text-2xl font-semibold tracking-tight">
          Cohort {last.n} vs cohort {prev.n}
        </h3>
        <p className="text-base text-muted-foreground mt-0.5 max-w-4xl leading-snug">
          The newest group of people against the group before it.{" "}
          <InfoHint label="Which cohorts these are">
            Always these two, whichever tab is selected above. Cohort {prev.n} is “
            {prev.label}”; cohort {last.n} is “{last.label}”.
          </InfoHint>
        </p>
      </div>
      <button
        type="button"
        onClick={onSeeAll}
        className="text-sm font-medium text-muted-foreground underline underline-offset-4 hover:text-foreground whitespace-nowrap"
      >
        See every cohort →
      </button>
    </div>
  )

  if (!execs) {
    return (
      <>
        {header}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 rounded-lg border bg-card animate-pulse" />
          ))}
        </div>
      </>
    )
  }

  const rows = changeRows(execs)

  return (
    <>
      {header}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => {
          const now = r.cells[r.cells.length - 1]
          const before = r.cells[r.cells.length - 2]
          const { d, tone, why, Arrow } = changeVerdict(r)
          return (
            <div key={r.label} className="rounded-lg border bg-card px-4 py-3" title={r.hint}>
              <div className="text-sm text-muted-foreground">{r.label}</div>
              <div className="flex items-baseline justify-between gap-3 mt-1">
                <span className="text-3xl font-semibold tabular-nums">
                  {fmtChangeCell(r.kind, now)}
                </span>
                {d == null ? (
                  <span className="text-sm text-muted-foreground/60">no read yet</span>
                ) : (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-base font-medium tabular-nums",
                      CHANGE_TONE[tone],
                    )}
                    title={why}
                  >
                    <Arrow className="size-4" />
                    {fmtChangeDelta(d)}
                  </span>
                )}
              </div>
              {/* Both sides in full, so the move above is never the only number:
                  "+9 pts" on 4 people is a coin flip, and it should look like one. */}
              <div className="text-sm text-muted-foreground tabular-nums mt-0.5">
                {fmtChangeBasis(r.kind, now) ?? fmtChangeCell(r.kind, now)}
                {before.num != null && (
                  <span className="text-muted-foreground/60">
                    {" "}
                    · was {fmtChangeCell(r.kind, before)}
                  </span>
                )}
              </div>
              {why && (
                <div className="text-xs text-muted-foreground/80 mt-1.5 leading-snug">{why}</div>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}

function CohortChangeCard({
  meta,
  execs,
  onbs,
}: {
  meta: CohortsResponse | null
  execs: (ExecutiveSummary | null)[] | null
  onbs: (OnboardingMetrics | null)[] | null
}) {
  if (!meta) return null
  if (!execs) {
    return (
      <section>
        <SectionHeading title="Change between cohorts" description="Reading each cohort on its own…" />
        <div className="h-72 rounded-lg border bg-muted animate-pulse" />
      </section>
    )
  }
  const last = meta.cohorts[meta.cohorts.length - 1]
  const prev = meta.cohorts[meta.cohorts.length - 2]
  const missing = meta.cohorts.filter((_, i) => !execs[i]).map((c) => c.n)
  const funnelRows = onbs ? funnelChangeRows(onbs) : []
  const accessRows = onbs ? accessChangeRows(onbs) : []

  return (
    <section className="space-y-8">
      <div>
        <SectionHeading
          title="Change between cohorts"
          description={`Every cohort side by side, as shares and per-user rates. The last column is cohort ${last?.n} against cohort ${prev?.n}.`}
        />
        <ChangeTable meta={meta} rows={changeRows(execs)} />
        <p className="mt-3 text-sm text-muted-foreground">
          <strong>A greyed change isn't a result.</strong>{" "}
          <InfoHint side="bottom" label="Why a change greys out">
            Either side rests on fewer than five people, or the row is cumulative
            and the older cohort simply had more weeks. Rates, not totals: cohort 1
            holds most of the roster, so a raw count would only restate that.
          </InfoHint>
          {missing.length > 0 && (
            <span className="text-amber-700">
              {" "}· Cohort {missing.join(", ")} failed to load this time — hit
              Refresh.
            </span>
          )}
        </p>
      </div>

      <div>
        <SectionHeading
          title="Signup steps"
          description="The drop-off card, one column per cohort. Signup happens in one sitting, so these compare straight across — no age handicap."
        />
        {onbs == null ? (
          <div className="h-64 rounded-lg border bg-muted animate-pulse" />
        ) : funnelRows.length === 0 ? (
          <div className="rounded-lg border bg-card p-4 text-base text-muted-foreground">
            No screen data came back for any cohort this load — hit Refresh.
          </div>
        ) : (
          <>
            <div className="mb-3 space-y-1">
              {meta.cohorts.map((c, i) => {
                const w = weakestStep(onbs[i])
                return (
                  <p key={c.n} className="text-base">
                    <span className="text-muted-foreground">Cohort {c.n} · </span>
                    {w ? (
                      <>
                        <span className="font-semibold text-red-600">
                          weakest step: {w.label}
                        </span>
                        <span className="text-muted-foreground">
                          {" "}
                          — {w.completed} of {w.reached} got through ({w.pass}%)
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">
                        no step has 10 people through it yet, so naming a weakest one
                        would be naming noise
                      </span>
                    )}
                  </p>
                )
              })}
            </div>
            <ChangeTable meta={meta} rows={funnelRows} />
            <p className="mt-3 text-sm text-muted-foreground">
              Every row is a pass rate — of the people who reached that screen,
              the share that went on.{" "}
              <InfoHint side="bottom" label="Two things that skew these rates">
                A weakest step needs 10 people through it, so a young cohort may not
                name one. The Name screen only started reporting on 2026-06-18; its
                rate covers whoever it saw, not everyone who passed.
              </InfoHint>
            </p>
          </>
        )}
      </div>

      <div>
        <SectionHeading
          title="Getting in the door"
          description="Waitlist, approval and account, per cohort. Read each row on its own — the stages do not chain within a cohort."
        />
        {onbs == null ? (
          <div className="h-48 rounded-lg border bg-muted animate-pulse" />
        ) : accessRows.length === 0 ? (
          <div className="rounded-lg border bg-card p-4 text-base text-muted-foreground">
            No waitlist data came back for any cohort this load — hit Refresh.
          </div>
        ) : (
          <>
            <ChangeTable meta={meta} rows={accessRows} />
            <p className="mt-3 text-sm text-muted-foreground">
              Read the rows one at a time — they aren't a staircase.{" "}
              <InfoHint side="bottom" label="Why the stages don't line up">
                Each stage is dated by its own event, so a batch approval files those
                people under the cohort we ran it in while their applications sit in
                an earlier one. The approval row is never scored, because a fresh
                batch hasn't had time to open the app.
              </InfoHint>
            </p>
          </>
        )}
      </div>
    </section>
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<Derived | null>(null)
  const [sla, setSla] = useState<BookingSlaMetrics | null>(null)
  const [posthog, setPosthog] = useState<PostHogMetrics | null>(null)
  const [bookingSummary, setBookingSummary] = useState<BookingSummary | null>(null)
  const [chatSummary, setChatSummary] = useState<ChatSummary | null>(null)
  const [onboarding, setOnboarding] = useState<OnboardingMetrics | null>(null)
  const [general, setGeneral] = useState<GeneralMetrics | null>(null)
  const [bookingDropoff, setBookingDropoff] = useState<BookingDropoff | null>(null)
  const [workouts, setWorkouts] = useState<WorkoutCompletionMetrics | null>(null)
  const [exec, setExec] = useState<ExecutiveSummary | null>(null)
  // Cohort windows + the server's own member->cohort assignment.
  const [cohortMeta, setCohortMeta] = useState<CohortsResponse | null>(null)
  // Which cohorts the tabs have on. EMPTY MEANS ALL — so no pick and every pick
  // send the same (absent) `cohorts` param and return identical numbers.
  const [picked, setPicked] = useState<number[]>([])
  // The Change tab: every cohort read on its own, so the moves are on one screen.
  const [compare, setCompare] = useState(false)
  const [cohortExecs, setCohortExecs] = useState<(ExecutiveSummary | null)[] | null>(null)
  const [cohortOnbs, setCohortOnbs] = useState<(OnboardingMetrics | null)[] | null>(null)
  // RevenueCat, fetched separately from the rest of the page load so a 503
  // (not configured) or a 403 (this endpoint is founder-only) can't blank it.
  const [rc, setRc] = useState<RcOverview | null>(null)
  // Bumped by Refresh. Re-runs both loaders and tells RevenueCat to skip its
  // 10-minute cache, so the button means "now", not "recently".
  const [deepDiveTab, setDeepDiveTab] = useState<DeepDiveTab>("workouts")
  const [reload, setReload] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [generalLoading, setGeneralLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("offseason-prod-banner-dismissed") === "true"
    } catch {
      return false
    }
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      setGeneral(null)
      setGeneralLoading(true)
      setError(null)
      try {
        // Onboarding's extra HogQL (paywall views) 504s when it shares PostHog
        // with the rest of this Promise.all, and the card then says "nobody
        // saw the paywall". Fetch it first so it has the warehouse to itself.
        const onboardingMetrics = await getOnboardingMetrics(picked).catch(() => null)
        // Must land BEFORE the batch: every windowed call below reads its
        // lookback from the cohort pick, so the boundaries have to be known
        // first. One extra round-trip, and the cards stop lying about "all time".
        const cohortsMeta = await getCohorts().catch(() => null)
        const days = windowDays(cohortsMeta, picked)
        // Feature Engagement (getGeneralMetrics) is 9 sequential PostHog
        // HogQL queries — each can take ~25s. If it's in this Promise.all the
        // whole dashboard stays on skeletons until it returns. Load everything
        // else first so executive summary / drop-off / retention actually
        // render; Feature Engagement fills in after.
        const [activity, unified, slaMetrics, phMetrics, bookingSum, chatSum, bookingDrop, workoutMetrics, execSummary] = await Promise.all([
          // Enough rows to actually fill the window: 200 covers a month, but a
          // year's chart built from 200 rows invents a ramp out of truncation.
          getActivityLog(days > 60 ? 2000 : 200),
          getUnifiedUsers().catch(() => [] as UnifiedUser[]),
          getBookingSlaMetrics(days, picked).catch(() => null),
          getPostHogMetrics(days, picked).catch(() => null),
          getBookingSummary(days, picked).catch(() => null),
          getChatSummary(days, picked).catch(() => null),
          getBookingDropoff(days, false, picked).catch(() => null),
          getWorkoutCompletionMetrics(picked).catch(() => null),
          getExecutiveSummary(picked).catch(() => null),
        ])
        if (cancelled) return
        setCohortMeta(cohortsMeta)
        // The two lists below feed tiles this page computes itself, so the
        // narrowing the server did for the other cards has to be repeated here
        // — using the server's assignment, not a second guess at it. `internal`
        // drops the waitlist-only dry runs, which have no profile id for
        // inCohorts to recognise them by.
        setData(
          computeDerived(
            activity.filter((e) => inCohorts(cohortsMeta, picked, e.user_id)),
            unified.filter(
              (u) => !u.internal && inCohorts(cohortsMeta, picked, u.user_id, u.approved_at),
            ),
            execSummary,
            days,
          ),
        )
        setSla(slaMetrics)
        setPosthog(phMetrics)
        setBookingSummary(bookingSum)
        setChatSummary(chatSum)
        setOnboarding(onboardingMetrics)
        setBookingDropoff(bookingDrop)
        setWorkouts(workoutMetrics)
        setExec(execSummary)
        setIsLoading(false)

        const generalMetrics = await getGeneralMetrics(days, picked).catch(() => null)
        if (cancelled) return
        setGeneral(generalMetrics)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load dashboard")
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
          setGeneralLoading(false)
        }
      }
    }
    load()
    // Switching tabs refetches everything — the cohort narrowing happens on the
    // server for most cards, so there is nothing to re-slice client-side.
    return () => {
      cancelled = true
    }
  }, [reload, picked.join(",")])

  // One executive_summary per cohort. Same endpoint the page already calls, with
  // the ?cohorts=N it already supports — nothing new on the backend.
  //
  // NOT gated on `compare`: the group-to-group strip under Volume
  // needs these on every load. They're database-only reads, so a handful of them
  // costs about what the one summary above already costs.
  useEffect(() => {
    if (!cohortMeta || cohortMeta.cohorts.length < 2) return
    let live = true
    setCohortExecs(null)
    async function load() {
      const execs = await Promise.all(
        cohortMeta!.cohorts.map((c) => getExecutiveSummary([c.n]).catch(() => null)),
      )
      if (!live) return
      setCohortExecs(execs)
    }
    load()
    return () => {
      live = false
    }
  }, [cohortMeta, reload])

  // The signup-step half of the comparison, still gated on `compare` — this one
  // is PostHog, and it has to go ONE AT A TIME. Onboarding's paywall HogQL 504s
  // when it shares PostHog with anything else, and the card then claims nobody
  // saw the paywall.
  useEffect(() => {
    if (!compare || !cohortMeta) return
    let live = true
    setCohortOnbs(null)
    async function load() {
      const onbs: (OnboardingMetrics | null)[] = []
      for (const c of cohortMeta!.cohorts) {
        onbs.push(await getOnboardingMetrics([c.n]).catch(() => null))
        if (!live) return
      }
      setCohortOnbs(onbs)
    }
    load()
    return () => {
      live = false
    }
  }, [compare, cohortMeta, reload])

  useEffect(() => {
    getRevenueCatOverview({ refresh: reload > 0 })
      .then(setRc)
      .catch(() => setRc(null))
  }, [reload])

  function dismissBanner() {
    setBannerDismissed(true)
    try {
      sessionStorage.setItem("offseason-prod-banner-dismissed", "true")
    } catch {}
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
        <div className="h-64 rounded-lg border bg-muted animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-56 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="size-8 mx-auto mb-2 text-destructive" />
          <p className="text-base text-destructive">{error ?? "No data"}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-4 py-1.5 text-base bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-8">
      {!bannerDismissed && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <ShieldAlert className="size-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-amber-900">
              Live Production Database
            </h3>
            <p className="text-sm text-amber-700 mt-1">
              You are connected to the live Supabase database that the Offseason mobile app and backend depend on.
              Changes made here are <strong>immediate</strong> and affect real users. Use read-only mode (toggle in header)
              when browsing. All destructive actions require explicit confirmation.
            </p>
          </div>
          <button
            onClick={dismissBanner}
            className="p-1 rounded hover:bg-amber-200 text-amber-600 transition-colors shrink-0"
            title="Dismiss for this session"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      )}

      <div>
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-2xl font-bold">Performance Dashboard</h1>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href="/glossary"
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border bg-background px-2.5 text-sm hover:bg-muted"
              title="What every metric on this page counts — opens in a new tab so you don't lose your place"
            >
              <BookOpen className="size-3.5" />
              Glossary
            </a>
            <Link
              to="/ops"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border bg-background px-2.5 text-sm hover:bg-muted"
              title="Cron health, the activity log, and the metrics we can't answer yet"
            >
              <Wrench className="size-3.5" />
              Ops
            </Link>
            <button
              onClick={() => setReload((n) => n + 1)}
              disabled={isLoading}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border bg-background px-2.5 text-sm hover:bg-muted disabled:opacity-50"
              title="Re-read every number, RevenueCat included, ignoring caches"
            >
              <RefreshCw className={cn("size-3.5", isLoading && "animate-spin")} />
              Refresh
            </button>
          </div>
        </div>
        <p className="text-base text-muted-foreground mt-1 max-w-3xl">
          Some tiles read{" "}
          <span className="inline-flex items-center gap-1 rounded-full border border-dashed px-1.5 py-0.5 text-sm uppercase tracking-wide text-muted-foreground align-middle">
            Not yet tracked
          </span>{" "}
          <InfoHint label="What that badge means">
            That tile is missing instrumentation, not missing usage — we'd rather
            say so than show a placeholder number.
          </InfoHint>
        </p>
        <div className="mt-3">
          <CohortTabs
            meta={cohortMeta}
            picked={picked}
            onChange={setPicked}
            compare={compare}
            onCompare={setCompare}
          />
        </div>
      </div>

      {compare ? (
        <CohortChangeCard meta={cohortMeta} execs={cohortExecs} onbs={cohortOnbs} />
      ) : (
        <>
      <KpiStrip exec={exec} general={general} />

      <section>
        <SectionHeading
          title="Retention & Growth"
          description="Waitlist growth and membership mix are live. True cohort retention needs a per-day activity signal we don't log yet — it sits under Not Yet Tracked on Ops."
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Waitlist signups, not accounts: the roster's `created_at` is first
              appearance anywhere, which is the waitlist form for almost everyone. */}
          <ChartCard
            title="Waitlist Growth"
            subtitle={
              <>
                Total on the list, every week on record{" "}
                <InfoHint label="How to read the curve">
                  The line carries forward: a week that adds 30 people starts
                  from wherever the week before ended, so the slope is the
                  growth rate — steep is a good week, flat is a stall. Hover a
                  point for that week’s own adds. Weeks run Pacific and end
                  today, so the last point covers a partial week.
                </InfoHint>
              </>
            }
          >
            {data.signups.length === 0 ? (
              <p className="text-base text-muted-foreground py-12 text-center">No signups on record</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.signups} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tickFormatter={fmtDay} tick={{ fontSize: 10 }} minTickGap={28} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelFormatter={(l) => `Week of ${l}`}
                    formatter={(v, _n, p) => [
                      `${v} on the list (+${(p as { payload?: { signups?: number } }).payload?.signups ?? 0} that week)`,
                      "Waitlist",
                    ]}
                  />
                  <Line type="monotone" dataKey="total" stroke={COLORS.signups} strokeWidth={2} dot={false} name="Waitlist" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {onboarding?.configured && !onboarding.error ? (
            <SubscriptionMixCard m={onboarding} rc={rc} />
          ) : null}

          <ChartCard
            title="Access Funnel"
            subtitle="The same five steps the Users & Access page counts"
          >
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.accessFunnel} layout="vertical" margin={{ top: 4, right: 12, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={72} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" fill={COLORS.violet} radius={[0, 3, 3, 0]} name="Users" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>

      {/*
        ORDER IS THE ARGUMENT, and it runs the way a person joins: how they got
        in (Onboarding) → what still stands between them and a plan (Blockers) →
        who got in, by name (Accounts) → what those people do once inside
        (Feature Engagement). Feature Engagement used to come first, which meant
        the page opened on usage rates whose denominator hadn't been introduced
        yet — you met "72 of 88" before anything said what 88 was.
      */}
      <GroupHeading
        title="Funnel"
        description="One person's path, in order: getting in, getting a plan, being on the list by name, then using the thing. Four sections that only mean anything read together."
      />

      <section>
        <SectionHeading
          title="Onboarding Metrics"
          description="Where new users get stuck, and who they are by name. Everything but the in-app screens comes from the database, matched on phone — those people are always named. Screens come from app activity, which is anonymous until someone logs in."
        />
        <OnboardingMetricsSection m={onboarding} />
      </section>

      <section>
        <SectionHeading
          title="What's Blocking a First Plan"
          description="The last answer each user still owes us before the coach can build them a season. Finishing signup and having a plan are two different finish lines — this is the second one, which is why it no longer shares a heading with the first."
        />
        <PlanBlockerSection m={onboarding} />
      </section>

      <section>
        <SectionHeading
          title="Accounts"
          description={
            onboarding && onboarding.configured && !onboarding.error
              ? `Every account we let in, newest first — ${onboarding.total_accounts ?? 0} of them, the same number the KPI strip divides by. ${onboarding.total_members ?? 0} finished onboarding and are the “users” the rest of this page counts; ${onboarding.stalled_in_signup ?? 0} did not and are marked in signup${onboarding.wiped_onboarding ? `, ${onboarding.wiped_onboarding} of those only because the July wipe blanked their row` : ""}.${onboarding.never_approved ? ` ${onboarding.never_approved} more reached signup without ever being approved and aren’t counted here — they’re accounted for on “Getting in the door” above.` : ""} Active today = did anything in the app today.`
              : "Every account we let in, newest first."
          }
        />
        <AccountsSection m={onboarding} rc={rc} />
      </section>

      <section>
        <SectionHeading
          title="Feature Engagement"
          description="How users reach each feature, and what they do once they're there. A feature we can't see still gets a card — missing tracking and missing usage look identical, and only one of them is our fault."
        />
        {generalLoading ? (
          <div className="h-48 rounded-lg border bg-muted animate-pulse" />
        ) : (
          <ErrorBoundary
            key={reload}
            fallback={
              <PlaceholderCard
                title="Feature Engagement"
                need="This section crashed while rendering. Hit Refresh to try again — the rest of the dashboard is still valid."
              />
            }
          >
            <FeatureEngagement g={general} users={onboarding?.total_members ?? null} />
          </ErrorBoundary>
        )}
      </section>

      <section>
        <SectionHeading
          title="Volume"
          description="What has actually happened, in counts — all time, or one cohort, following the tab above. The strip at the top is how we are doing; this is how much of it there has been."
        />
        {exec ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 rounded-lg border bg-card px-6 py-2">
            <div className="divide-y">
              <ExecMetricRow {...payingUsers(exec.metrics.paying_users, rc, cohortMeta, picked)} />
              {/* Active seasons is gone from here on purpose — "Has a live plan"
                  in the strip is the same fact as a share of users, which is
                  the readable half of it. Approved stays: it is the top of the
                  funnel and lives nowhere else on this page. */}
              <ExecMetricRow m={exec.metrics.total_accounts} />
              <ExecMetricRow m={exec.metrics.workouts_completed} />
            </div>
            <div className="divide-y border-t md:border-t-0 md:border-l md:pl-12">
              <ExecMetricRow m={exec.metrics.studio_bookings} />
              <ExecMetricRow m={exec.metrics.booking_speed} />
            </div>
          </div>
        ) : (
          <PlaceholderCard
            title="Volume"
            need="Couldn't reach /admin/executive_summary this load."
          />
        )}
        <CohortMoveStrip
          meta={cohortMeta}
          execs={cohortExecs}
          onSeeAll={() => setCompare(true)}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <HoverTile
            title="AI Activity (7d)"
            value={data.aiActivity7d}
            icon={Activity}
            delta={data.aiActivityDelta}
            subtitle="Generations + extensions · hover for split, click for detail"
            onClick={() => navigate("/ai-generations")}
            hoverTitle="Last 7 days · by type"
            rows={[
              { label: "Generated", value: data.aiTypeBreakdown7d.generated, color: COLORS.generated },
              { label: "Extended", value: data.aiTypeBreakdown7d.extended, color: COLORS.extended },
              { label: "Overwritten", value: data.aiTypeBreakdown7d.overwritten, color: COLORS.overwritten },
            ]}
            footer="Click the tile for the full breakdown →"
          />
          <ScoreCard
            title="Active Today"
            value={onboarding?.active_today ?? "—"}
            icon={Activity}
            subtitle={
              onboarding?.total_accounts
                ? `of ${onboarding.total_accounts} accounts · see who, in Accounts above`
                : "Anyone who did anything in the app today"
            }
          />
        </div>
      </section>

      <section>
        <SectionHeading
          title="Feature Deep Dive"
          description="One feature at a time — how much of it gets used, and where it breaks. Same cards and same queries as before; the tabs just stop four features competing for one screen."
        />
        <Tabs value={deepDiveTab} onValueChange={(v) => setDeepDiveTab(v as DeepDiveTab)}>
          <TabsList>
            <TabsTrigger value="workouts">Workouts</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="biometrics">Biometrics</TabsTrigger>
          </TabsList>

          <TabsContent value="workouts" className="space-y-4 mt-4">
            <p className="text-base text-muted-foreground">
              Every workout day we&apos;ve written for a user, what kind of training it was,
              and whether they told us it got done.{" "}
              <InfoHint label="Where this comes from">
                The seasons table, all-time.
              </InfoHint>
            </p>
            <WorkoutsCompletedSection w={workouts} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard
                title="Workout Generation Activity"
                subtitle={
                  <>
                    {data.trendGrain === "week" ? "Weekly" : "Daily"} generated /
                    extended / overwritten · {windowLabel(data.trendDays)}{" "}
                    <InfoHint label="Where this comes from">
                      Read off the most recent 200 activity-log events, so a busy
                      window can start later than the label suggests.
                    </InfoHint>
                  </>
                }
              >
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={fmtDay}
                      tick={{ fontSize: 10 }}
                      // ~6 labels whatever the window is; a fixed interval crams
                      // them into an unreadable smear once the window widens.
                      interval={Math.max(0, Math.ceil(data.trend.length / 6) - 1)}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelFormatter={(l) => (data.trendGrain === "week" ? `Week of ${l}` : `Date ${l}`)}
                    />
                    <Bar dataKey="generated" stackId="a" fill={COLORS.generated} name="Generated" />
                    <Bar dataKey="extended" stackId="a" fill={COLORS.extended} name="Extended" />
                    <Bar dataKey="overwritten" stackId="a" fill={COLORS.overwritten} name="Overwritten" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-3 mt-2 text-base text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="size-2 rounded-sm" style={{ background: COLORS.generated }} /> Generated</span>
                  <span className="flex items-center gap-1"><span className="size-2 rounded-sm" style={{ background: COLORS.extended }} /> Extended</span>
                  <span className="flex items-center gap-1"><span className="size-2 rounded-sm" style={{ background: COLORS.overwritten }} /> Overwritten</span>
                </div>
              </ChartCard>
              {posthog?.configured && posthog.edit_rate_pct != null ? (
                <div className="space-y-4">
                  <StaleBanner asOf={posthog.stale_as_of} />
                  <ScoreCard
                    title={`Edit Rate (${windowLabel(posthog.window_days)})`}
                    value={`${posthog.edit_rate_pct}%`}
                    icon={Activity}
                    subtitle={`${posthog.edits ?? "—"} edits ÷ ${posthog.generated ?? "—"} generated · via PostHog`}
                  />
                </div>
              ) : (
                <PlaceholderCard
                  title="Edit Rate + Edit-Type Breakdown"
                  need={
                    posthog && !posthog.configured
                      ? "Edit RATE is ready — set the PostHog read key (diff_applied ÷ season_generated). The TYPE breakdown still needs a persisted edit log."
                      : // An outage used to land in the line below, which says "not
                        // built yet" — so a PostHog blip read as a missing feature.
                        // Different lie from a false zero, same cause: the endpoint's
                        // `error` was never checked here.
                        posthog?.error
                        ? "Couldn't reach PostHog for the edit rate this load — refresh. (The TYPE breakdown separately needs a persisted edit log.)"
                        : "Edit rate via PostHog (diff_applied ÷ season_generated). Type breakdown needs a persisted edit log."
                  }
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="space-y-4 mt-4">
            <p className="text-base text-muted-foreground">
              Where a booking attempt stops.{" "}
              <InfoHint label="Where this comes from">
                The drop-off funnel is from app activity, not the bookings table —
                that lost its history in the July wipe. Our own accounts are
                included on purpose.
              </InfoHint>
            </p>
            <BookingDropoffSection b={bookingDropoff} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StudioPerkCard summary={bookingSummary} cohorts={picked} />
              {bookingSummary ? (
                <ChartCard
                  title="Booking Volume + Conversion"
                  subtitle={`${windowLabel(bookingSummary.window_days)} · from the bookings table`}
                >
                  <div className="grid grid-cols-2 gap-4 py-2">
                    <div>
                      <div className="text-2xl font-bold">{bookingSummary.total}</div>
                      <div className="text-base text-muted-foreground">Bookings initiated</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {bookingSummary.conversion_rate_pct != null ? `${bookingSummary.conversion_rate_pct}%` : "—"}
                      </div>
                      <div className="text-base text-muted-foreground">
                        Conversion ({bookingSummary.confirmed}/{bookingSummary.resolved} resolved)
                      </div>
                    </div>
                  </div>
                  {Object.keys(bookingSummary.status_breakdown).length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-base text-muted-foreground">
                      {Object.entries(bookingSummary.status_breakdown).map(([k, v]) => (
                        <span key={k} className="rounded-full border px-1.5 py-0.5">{k}: {v}</span>
                      ))}
                    </div>
                  )}
                </ChartCard>
              ) : (
                <PlaceholderCard
                  title="Booking Volume + Conversion"
                  need="Couldn't load the booking summary this load (DB-sourced from the bookings table)."
                />
              )}
              {/*
                ONE SOURCE OF TRUTH for booking conversion: the Volume + Conversion
                card above. This card used to print its own "Success rate" and its own
                status chips, and both were the SAME numbers arrived at the same way —
                /bookings/sla_metrics and /bookings/summary read the same table with
                the same cohort filter and the same confirmed÷resolved formula. Two
                tiles that can never disagree are not corroboration, they're a second
                place to have to fix. Deleted here; this card keeps only what the
                telemetry alone knows — how long the agent took and what it cost.
              */}
              {sla && sla.total_attempts > 0 ? (
                <ChartCard
                  title="Agent Run Time (P50 / P90) + Success Rate"
                  subtitle={
                    <>
                      {windowLabel(sla.window_days)} · {sla.total_attempts}{" "}
                      attempts, every outcome{" "}
                      <InfoHint label="Why this differs from Booking speed">
                        One agent leg — a different clock from Booking speed above,
                        which counts confirmed bookings only.
                      </InfoHint>
                    </>
                  }
                >
                  <div className="grid grid-cols-2 gap-4 py-2">
                    <div>
                      <div className="text-2xl font-bold">
                        {sla.latency_ms.p50 != null ? `${(sla.latency_ms.p50 / 1000).toFixed(1)}s` : "—"}
                      </div>
                      <div className="text-base text-muted-foreground">P50 run time</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {sla.latency_ms.p90 != null ? `${(sla.latency_ms.p90 / 1000).toFixed(1)}s` : "—"}
                      </div>
                      {/* The sample is smaller than total_attempts — only runs that
                          persisted telemetry have a duration — so it's named, not
                          left to read as all of them. */}
                      <div className="text-base text-muted-foreground">
                        P90 run time · {sla.latency_ms.sample_size} timed
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{sla.avg_iterations ?? "—"}</div>
                      <div className="text-base text-muted-foreground">Avg agent iterations</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{(sla.tokens.total / 1000).toFixed(0)}k</div>
                      <div className="text-base text-muted-foreground">Tokens (window)</div>
                    </div>
                  </div>
                </ChartCard>
              ) : (
                <PlaceholderCard
                  title="Agent Run Time (P50 / P90) + Success Rate"
                  need={`No bookings in the ${windowLabel(sla?.window_days)} to compute an SLA from.`}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="chat" className="space-y-4 mt-4">
            <p className="text-base text-muted-foreground">
              Coach conversations — how many threads, how deep they go, and how many users
              are actually talking to us. Read the messages themselves in Chat Transcripts.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {chatSummary ? (
                <ChartCard
                  title="Chat Sessions + Volume"
                  subtitle={`${windowLabel(chatSummary.window_days)} · from chat_messages`}
                >
                  <div className="grid grid-cols-2 gap-4 py-2">
                    <div>
                      <div className="text-2xl font-bold">{chatSummary.messages}</div>
                      <div className="text-base text-muted-foreground">Messages ({chatSummary.user_messages} from users)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{chatSummary.sessions}</div>
                      <div className="text-base text-muted-foreground">Sessions · {chatSummary.unique_users} users</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{chatSummary.msgs_per_session ?? "—"}</div>
                      <div className="text-base text-muted-foreground">Msgs / session</div>
                    </div>
                  </div>
                </ChartCard>
              ) : (
                <PlaceholderCard
                  title="Chat Sessions + Topics"
                  need="Couldn't load the chat summary this load (DB-sourced from chat_messages)."
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="biometrics" className="space-y-4 mt-4">
            <p className="text-base text-muted-foreground">
              What wearables tell us about a user before the workout is written.
            </p>
            {/*
              An empty tab is the honest state, not an oversight: nothing on this page
              reads the biometric stream. The one wearable signal we do render — "Wearable
              connected" and the brand split — is a property of an account, so it stays in
              the Accounts roster where it is searchable next to the person. Say so here
              rather than duplicate the tile, or a reader concludes the tab is broken.
            */}
            <PlaceholderCard
              title="Readiness + Recovery Trend"
              need={`Not on this dashboard yet. Connection counts live in Accounts above ("Wearable connected" + the brand split); the per-user daily readings are on the Wearables page, one user at a time. A trend here needs an endpoint that aggregates them.`}
            />
          </TabsContent>
        </Tabs>
      </section>

      <p className="flex items-center gap-1.5 text-base text-muted-foreground">
        <Info className="size-3.5" />
        Season coverage and the per-user table now live on the{" "}
        <Link to="/users" className="inline-flex items-center gap-0.5 font-medium text-foreground hover:text-primary">
          Users tab <ArrowRight className="size-3" />
        </Link>
        .
      </p>
        </>
      )}
    </div>
  )
}
